import Icon from "components/Icons/Icon";
import TravelWidget from "components/b2c/explore/TravelWidget";
import { CircleFlag, PhotoFill } from "components/b2c/explore/exploreCommon";
import useAppStoreLink, { usePlacementOnelinkUrl } from "components/common/useAppStoreLink";
import { forQrCode, useQrCodeCanvas } from "components/common/useAppsflyerOneLink";
import AppStoreButtons from "components/downloadappcta/appstorebuttons/AppStoreButtons";
import type { TripPreviewMapDestination } from "components/map/TripPreviewMap";
import HeroSearchCard from "components/website/components/HeroSearchCard";
import { isRoutePreviewHref, withRoutePreviewLang } from "components/website/pages/articleTripHref";
import { PlanGateModal, usePlanGate } from "components/website/components/planGate";
import qrCode from "components/website/img/qr-download-app.png";
import { t as lookupUi } from "i18n/index";
import { discoverPath, localizeHref, type MarketingLangCode } from "i18n/urlLocale";
import { useMarketingLang } from "i18n/useMarketingLang";
import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { isMobileOnly } from "react-device-detect";
import { useHistory } from "react-router-dom";
import { INLINE_MD, stripInlineMd } from "utils/inlineMarkdown";
import { coverSlotWidth, sizedPhoto, srcSetFor } from "utils/photoUrl";
import { slugify } from "utils/urlUtils";
import type { GuideMapMarker } from "./GuideMap";
import type { CityData } from "./cityData";
import type { CountryData, GuideHeadings, MediaCredit, MonthRating } from "./countryData";
import { openRouteMapTab, scrollToRouteMap } from "./countryMap/routeStore";
import styles from "./discoverSections.module.scss";

export { stripInlineMd };

/**
 * Lazy on purpose, and it is the single biggest lever on what a marketing visitor downloads.
 *
 * GuideMap statically imports maplibre-gl (~1.8 MB). These sections are reachable from the
 * marketing entry bundle now that /discover is SSR'd, so a static import here puts MapLibre in
 * front of every homepage and blog visitor for a map only the guide pages render — and only
 * after mount, since it cannot be server-rendered at all.
 *
 * Suspense boundaries are per usage site rather than one high up: MarketingShell's header
 * explains why a top-level boundary turns recoverable hydration warnings into a blank page.
 */
const GuideMap = lazy(() => import("./GuideMap"));

/** Holds the map's space while its chunk loads, and during SSR, where it never renders. */
const MapFallback = () => <div className={styles.mapFallback} aria-hidden="true" />;

/**
 * Backs both `LazyGuideMap` below and `RouteCard`'s embed iframe further down this file: mounts
 * a WebGL-heavy child only once its slot is actually on screen. A country hub mounts 3 in-page
 * maps plus up to 12 route-card iframes, each booting its own MapLibre/WebGL context — 27 on a
 * typical page. Chrome caps live WebGL contexts at ~16 per page and silently kills the OLDEST
 * one past the cap, which was always one of the 3 in-page maps: they mount first, so a country
 * hub rendered its hero/sidebar/mobile maps as blank white cards with the markers floating on
 * nothing (123yb69326d).
 *
 * `exitRootMargin` turns this into a two-observer gate with hysteresis: an "enter" observer
 * flips `inView` true within `rootMargin` of the viewport, and a separate "exit" observer flips
 * it false only once the element is outside the (wider) `exitRootMargin`. Between the two
 * thresholds neither observer changes the verdict, so ordinary scroll jitter never thrashes a
 * mount/unmount cycle. Leave `exitRootMargin` out to latch instead — once `inView` goes true it
 * never goes false again, which is what `LazyGuideMap` wants: these maps carry camera state and
 * are cheap to keep mounted, not cheap to re-fit every time they scroll off and back on.
 *
 * Both margins apply to the ROOT only. IntersectionObserver still clips the target against every
 * intervening scroll container, and `rootMargin` does not widen those clips — so for a route card
 * inside `.routesScroller` (`overflow-x: auto`) the margins buy nothing horizontally: a card is
 * "in view" exactly while it is inside the carousel's own visible strip, and the hysteresis only
 * has an effect when the page scrolls vertically past the section. That is the behaviour we want
 * here — it holds the live-embed count to the four or five cards actually on screen — but it does
 * mean a card that scrolls out and back reloads its embed rather than resuming. Acceptable for a
 * decorative thumbnail; do not reuse this hook for anything whose state must survive a scroll.
 */
function useInViewport<T extends Element>(ref: React.RefObject<T>, options: { rootMargin: string; exitRootMargin?: string }): boolean {
	const { rootMargin, exitRootMargin } = options;
	const [inView, setInView] = useState(false);

	useEffect(() => {
		const el = ref.current;
		if (!el) return undefined;
		// No IntersectionObserver (old browsers, some test environments) — mount immediately
		// rather than never. Same fallback as useAffiliateCardTracking.ts:68 and
		// HomeMarketing.tsx:61.
		if (typeof IntersectionObserver === "undefined") {
			setInView(true);
			return undefined;
		}

		if (exitRootMargin === undefined) {
			const observer = new IntersectionObserver(
				([entry]) => {
					if (entry.isIntersecting) {
						setInView(true);
						observer.disconnect();
					}
				},
				{ rootMargin, threshold: 0 }
			);
			observer.observe(el);
			return () => observer.disconnect();
		}

		const enter = new IntersectionObserver(([entry]) => entry.isIntersecting && setInView(true), { rootMargin, threshold: 0 });
		const exit = new IntersectionObserver(([entry]) => !entry.isIntersecting && setInView(false), { rootMargin: exitRootMargin, threshold: 0 });
		enter.observe(el);
		exit.observe(el);
		return () => {
			enter.disconnect();
			exit.disconnect();
		};
	}, [ref, rootMargin, exitRootMargin]);

	return inView;
}

/**
 * The map, but kept out of the hydration pass — and out of the DOM until its slot is visible.
 *
 * GuideMap cannot be server-rendered at all, so a bare <Suspense> around it ships to the
 * browser as a *dehydrated* boundary: SSR emitted the fallback, and React has to hydrate that
 * boundary while the MapLibre chunk is still downloading. Any state update landing in that
 * window makes React abandon the boundary and switch it to client rendering — error #421. A
 * guide page mounts three maps and threw it three times on every load.
 *
 * Holding the plain fallback until after mount takes the boundary out of hydration entirely:
 * server output and first client render are the same placeholder, and the Suspense boundary
 * first appears in a later commit, where a suspending child is ordinary. No visual change —
 * the map could not paint before mount either way.
 *
 * `useInViewport` adds a second gate on top of `mounted`: even after mount, the map stays a
 * placeholder until it is within 200px of the viewport. See the comment above that hook for
 * why — this is the fix for 123yb69326d on the 3 in-page maps' side.
 */
function LazyGuideMap(props: React.ComponentProps<typeof GuideMap>) {
	const [mounted, setMounted] = useState(false);
	const ref = useRef<HTMLDivElement>(null);
	const inView = useInViewport(ref, { rootMargin: "200px" });
	useEffect(() => setMounted(true), []);
	return (
		<div ref={ref} style={{ width: "100%", height: "100%" }}>
			{mounted && inView ? (
				<Suspense fallback={<MapFallback />}>
					<GuideMap {...props} />
				</Suspense>
			) : (
				<MapFallback />
			)}
		</div>
	);
}

/**
 * The static route-preview map (`components/map/TripPreviewMap`), lazy for the same reason as
 * `LazyGuideMap` — it statically pulls in maplibre-gl + turf, which must not sit in the base
 * marketing bundle. Kept out of the hydration pass the same way. Used by the itinerary
 * article's hero and sidebar to draw the route in visiting order.
 */
const TripPreviewMap = lazy(() => import("components/map/TripPreviewMap"));

function LazyTripPreviewMap(props: React.ComponentProps<typeof TripPreviewMap>) {
	const [mounted, setMounted] = useState(false);
	useEffect(() => setMounted(true), []);
	if (!mounted) return <MapFallback />;
	return (
		<Suspense fallback={<MapFallback />}>
			<TripPreviewMap {...props} />
		</Suspense>
	);
}

type MonthGuide = CityData["months"][number];

// Every "guide" icon (country + destination pages) resolves to a chunky Icons.json entry —
// name maps 1:1 to the Icons.json key, suffixed with "--chunky".
const CHUNKY_ICON_MAP: Record<string, string> = {
	budget: "money-bag--chunky",
	packing: "backpack--chunky",
	calendar: "calendar--chunky",
	route: "map--chunky",
	ai: "sparkle--chunky",
	esim: "wifi--chunky",
	documents: "documents--chunky",
	bed: "bed--chunky",
	town: "town--chunky",
	city: "city--chunky",
	island: "island--chunky",
	region: "region--chunky",
	neighbourhood: "neighbourhood--chunky",
	nationalPark: "nationalPark--chunky",
	skiResort: "skiResort--chunky",
	heritage: "heritage--chunky",
	map: "map--chunky",
	marker: "marker--chunky",
	heart: "heart--chunky",
	eatdrink: "eatdrink--chunky",
	specialdays: "specialdays--chunky",
	practicalities: "practicalities--chunky",
	destinations: "destinations--chunky",
	visum: "visum--chunky",
	sun: "sun--chunky",
	culture: "culture--chunky",
	person: "person--chunky",
	faq: "faq--chunky",
	phrases: "phrases--chunky",
	naturalFeature: "naturalFeature--chunky",
};
export type GuideIconName = keyof typeof CHUNKY_ICON_MAP;

export function GuideIcon({ name, fill = "black", size = 20 }: { name: GuideIconName; fill?: string; size?: number }) {
	return <Icon name={CHUNKY_ICON_MAP[name] as any} fill={fill} size={size} />;
}

// Shared tilt class arrays — indices mirror the original rotate-degree arrays 1:1.
const NUTSHELL_TILTS = [styles.tiltNeg4, styles.tilt3, styles.tiltNeg3, styles.tilt4, styles.tiltNeg3, styles.tilt3];
const FOR_WHO_TILTS = [styles.tiltNeg25, styles.tilt2, styles.tiltNeg2, styles.tilt25, styles.tiltNeg15, styles.tilt2];
const SEASON_TONES = ["#13c892", "#e8c96a", "#7db8e0"];
const SEASON_TONE_CLASSES = [styles.seasonToneBest, styles.seasonToneOk, styles.seasonTonePoor];
const NEIGHBOURHOOD_TILTS = [styles.tiltNeg3, styles.tilt25, styles.tiltNeg2, styles.tilt3, styles.tiltNeg25];
const STAY_TILTS = [styles.tiltNeg3, styles.tilt25, styles.tiltNeg2, styles.tilt3];
const LOCAL_TIPS_TILTS = [styles.tiltNeg3, styles.tilt25, styles.tiltNeg2, styles.tilt3, styles.tiltNeg25, styles.tilt2];
const TILT_CLASS_BY_DEGREE: Record<number, string> = {
	[-4]: styles.tiltNeg4,
	[-3]: styles.tiltNeg3,
	[-2.5]: styles.tiltNeg25,
	[-2]: styles.tiltNeg2,
	[-1.5]: styles.tiltNeg15,
	2: styles.tilt2,
	2.5: styles.tilt25,
	3: styles.tilt3,
	4: styles.tilt4,
};

// ── Section chrome (headings, pills, lists) — shared visual language for the whole guide ──────────
/**
 * Per-geo heading overrides, supplied by the page and read by whichever section wants one.
 *
 * Context rather than a prop because the alternative is threading the same object through
 * seventeen call sites in two page components, where the failure mode is passing it to sixteen
 * of them. A section opts in by knowing its own id, which it already declares as its anchor.
 */
export const GuideHeadingsContext = React.createContext<GuideHeadings | undefined>(undefined);

/**
 * UI-chrome dictionary for the shared guide/article sections (kickers, CTA copy, table
 * headers, …). Provided by the page: the blog article renderers pass the resolved
 * `getBlogTranslations(article.locale)` map. When no provider is present — the `/discover`
 * country/city guide pages don't set one yet — `useGuideUi()` returns the English literal
 * passed as the fallback, so those pages render exactly as before.
 */
export const GuideUiContext = React.createContext<Record<string, string> | undefined>(undefined);

export function useGuideUi(): (key: string, fallback: string) => string {
	const dict = React.useContext(GuideUiContext);
	return React.useCallback((key: string, fallback: string) => lookupUi(dict ?? {}, key, fallback), [dict]);
}

/**
 * The reader's locale, threaded down to the one component that needs it purely to localise a
 * link it renders: `RichText`'s markdown-link branch (`localizeHref`, 123yb692y18). Blog prose
 * and breadcrumb hrefs arrive off the knowledge base unprefixed on purpose (see `localizeHref`'s
 * doc comment), so a body link rendered as-is dropped a reader out of the locale they were on —
 * `blockRegistry.tsx`'s read-more/CTA/related-card blocks already thread `lang` this way, prose
 * never did.
 *
 * Deliberately NOT `useMarketingLang()` — see the comment on `Hero`'s own `lang` above: that hook
 * falls back to `"en"` during SSR (no `window` yet), which would both emit wrong server HTML and
 * risk a hydration mismatch on first paint. This context is provided at the route root instead,
 * from whichever value is already SSR-correct there (`props.lang` on the two guide pages,
 * `articleLang` on the article page), and defaults to `"en"` so a caller that never provides it
 * renders exactly as it did before this context existed.
 */
export const GuideLangContext = React.createContext<MarketingLangCode>("en");

/**
 * The byline minus its "Updated {date}" clause, for a guide whose dossier has never recorded an
 * `updated_at` (123yb69326q).
 *
 * `guide.chrome.byline` bakes the date clause into one translated string in all seven locale
 * files, so there is no key to look up for the dateless variant. Deriving it structurally — split
 * on the separator, drop the clause holding `{date}` — works against all seven as they are
 * written today, and is the right call: it reuses copy the translators already wrote instead of
 * inventing a second string in six languages.
 *
 * What it must not do is fail silently when that shape stops holding, which a plain split does in
 * two different ways. Measured, not hypothesised:
 *
 *   separator drifts to "—"  ->  split yields ONE clause, it holds `{date}`, filtering leaves
 *                                nothing, and the byline renders as the EMPTY STRING
 *   `{date}` moves clause    ->  the token survives, and `fillUi` renders the literal "{date}"
 *                                on the page, because it falls back to `{key}` for a missing var
 *
 * Both are invisible to typecheck, to lint, and to any test written in English — the English
 * string is the one that always matches. So the derivation is checked, and falls back to the
 * English default this file owns rather than to a blank line. An English byline on a geo nobody
 * has verified is a small, honest loss; an empty one is a defect.
 */
export function bylineWithoutDate(template: string, englishDefault: string): string {
	const derived = template
		.split(" · ")
		.filter((clause) => !clause.includes("{date}"))
		.join(" · ");
	// `.trim()`, not `kept.length`: an empty or whitespace-only template survives the filter as a
	// single blank clause, so a length check passes it straight through and the byline is blank
	// anyway. Caught by the "never returns an empty string" case in bylineWithoutDate.test.ts.
	if (!derived.trim() || derived.includes("{date}")) {
		return englishDefault
			.split(" · ")
			.filter((clause) => !clause.includes("{date}"))
			.join(" · ");
	}
	return derived;
}

/** `useGuideUi()` returns plain strings — fill `{token}` placeholders after the lookup. */
export function fillUi(str: string, vars: Record<string, string>): string {
	return str.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? `{${k}}`);
}

/**
 * The eyebrow and H2 for `id`.
 *
 * Resolution order is: **per-geo knowledge-base override → locale dictionary → the English
 * literal the component ships with.** The KB override stays highest — it is hand-written per
 * geo (2 of 104 publishable geos have a copy file today, so the literal is what almost every
 * page still renders) and must not be clobbered by a generic translation. An empty string at
 * any tier is treated as absent, since a blank heading is a bug in every case and never an
 * instruction.
 *
 * The dictionary tier reads `guide.section.<dictId ?? id>.kicker` / `.h2` off `GuideUiContext`
 * (see its doc comment) and, when found, fills `{token}`s in it via `fillUi` — `h2`/`kicker`
 * themselves never carry a token, because every existing caller already interpolates the real
 * value into them before this hook ever sees it (a JSX child like `What not to miss in
 * {guide.name}`, or a template string built the same way), so there is nothing left to fill on
 * that side.
 *
 * `dictId` lets a caller point the dictionary lookup at a different key than its anchor `id` —
 * needed exactly once today: the country and destination "when" sections share the DOM anchor
 * `id="when"` (each page mounts only one of them, so the anchor never collides), but their H2s
 * read differently ("visit" vs "go to") and so cannot share one dictionary entry.
 *
 * `useDictionary` lets a caller opt out of the dictionary tier entirely. `Faq` is the one caller
 * that needs this: its `title` prop, when a blog article supplies one, arrives already resolved
 * from that article's own translation key (`blog.article.faqTitle`) with the real name filled
 * in. `useHeading` has no way to tell "the component's own literal" apart from "a caller's
 * already-resolved override" — both arrive as the same `h2: ReactNode` — so without this escape
 * hatch the dictionary tier would silently overwrite an already-correct article heading with an
 * unfilled `{name}` (no `vars` are threaded through that call site to fill it back in).
 */
export function useHeading(
	id: string,
	kicker: string,
	h2: React.ReactNode,
	opts?: { vars?: Record<string, string>; dictId?: string; useDictionary?: boolean }
): { kicker: string; h2: React.ReactNode; intro?: string } {
	const override = React.useContext(GuideHeadingsContext)?.[id];
	const ui = useGuideUi();
	const useDictionary = opts?.useDictionary ?? true;
	const dictId = opts?.dictId ?? id;
	const vars = opts?.vars ?? {};
	const dictKicker = useDictionary ? ui(`guide.section.${dictId}.kicker`, "") : "";
	const dictH2 = useDictionary ? ui(`guide.section.${dictId}.h2`, "") : "";
	return {
		kicker: override?.kicker?.trim() || (dictKicker ? fillUi(dictKicker, vars) : kicker),
		h2: override?.h2?.trim() || (dictH2 ? fillUi(dictH2, vars) : h2),
		intro: override?.intro?.trim() || undefined,
	};
}

export function SectionHeading({
	id,
	kicker,
	children,
	icon,
	tilt = -4,
	vars,
	dictId,
	useDictionary,
}: {
	id: string;
	kicker: string;
	children: React.ReactNode;
	icon: GuideIconName;
	tilt?: number;
	/** `{name}` etc. to fill into the locale-dictionary kicker/H2, when one exists for this section. */
	vars?: Record<string, string>;
	/** Dictionary key to consult when it must differ from the anchor `id` — see the `useHeading` doc comment. */
	dictId?: string;
	/** False to skip the dictionary tier — see the `useHeading` doc comment. */
	useDictionary?: boolean;
}) {
	const heading = useHeading(id, kicker, children, { vars, dictId, useDictionary });
	return (
		<div className={styles.sectionHeading} id={id}>
			<span className={`${styles.sectionHeadingIcon} ${TILT_CLASS_BY_DEGREE[tilt] ?? styles.tiltNeg4}`}>
				<GuideIcon name={icon} fill="currentColor" size={20} />
			</span>
			<span className={styles.sectionHeadingKicker}>{heading.kicker}</span>
			<h2 className={styles.sectionHeadingTitle}>{heading.h2}</h2>
			{/* Optional per-section lead-in. No block has one until a copy deck writes it, so
			    every page renders exactly as before until then. */}
			{heading.intro && (
				<p className={styles.sectionHeadingIntro}>
					<RichText text={heading.intro} />
				</p>
			)}
		</div>
	);
}

/**
 * Inline markdown, rendered.
 *
 * Every prose layer feeding these pages is markdown written by hand — the fact dossiers, the
 * `copy/` layer, the blog articles — and all of them use `**bold**` for a paragraph's lead-in
 * ("**Andaman coast** (Phuket, Krabi): go."). Nothing ever converted it, so the asterisks
 * rendered literally: 22 on the Thailand country page alone, plus every blog article. The
 * legacy Strapi blog never showed this because it ran the body through `ReactMarkdown`; these
 * pages take plain strings and printed them verbatim.
 *
 * Parsed into React elements rather than injected as HTML. The content is ours and reviewed,
 * so this is not about distrusting it — it is that `dangerouslySetInnerHTML` on the one code
 * path every page's prose flows through is a bad thing to leave lying around.
 *
 * Inline only — bold, italic, links, code. Block constructs are the exporters' job and arrive
 * as structure, not as text.
 */
// Single `_italic_` is here for the same reason `*italic*` is: the corpus writes both, and
// without it the markers print. The Milan article's "Quick index" shipped ten entries reading
// `_Ratanà: Modern Milanese Cuisine_` (123yb690uz0). It carries the SAME lookarounds as the
// asterisk alternative, and for a sharper reason: `_` is a word character in identifiers, so a
// naive rule eats the middle of `snake_case_name` and `some_file_path` and italicises it.
// The bold alternative permits single `*` inside, and an optional one immediately before the
// closing `**` so `***` terminating a nested italic is consumed whole. `[^*]+` could match
// neither, so `**Dry season / *verano***` matched *nothing* and printed all its markers —
// the dossiers nest italics inside bold constantly (loan words, local names).

/**
 * `plain` strips the emphasis markers instead of honouring them.
 *
 * For slots the stylesheet already sets to 600 weight — a practicalities lead line, a quick
 * fact's value, a stat — `**bold**` in the source is the dossier marking its own lead sentence,
 * not a request for emphasis relative to its neighbours. Rendering `<strong>` inside text that
 * is already bold says nothing, and leaving the asterisks says less: on the Thailand guide both
 * happened at once, a bold sentence with `**` printed around it.
 */
/**
 * The same markers RichText understands, removed from a plain string.
 *
 * For dossier values that are *interpolated* rather than rendered — the hero facts put them
 * inside their own `<strong>`, and lowercase them first — so a node is no use and nesting
 * emphasis inside emphasis says nothing anyway. Without this, `**in transition**` reached the
 * page with its asterisks intact inside an already-bold fact line.
 */

export function RichText({ text, plain = false }: { text: React.ReactNode; plain?: boolean }) {
	// Read unconditionally, before the early return below — Rules of Hooks. Defaults to "en" via
	// `GuideLangContext`'s own default when no page has provided one, which reproduces today's
	// unprefixed hrefs exactly.
	const lang = React.useContext(GuideLangContext);
	// Several of these slots are typed `ReactNode` because the hardcoded fallback data puts
	// elements in them. Only a string can carry markdown; anything else passes through, which
	// also means a caller never has to guard before using this.
	if (typeof text !== "string") return <>{text}</>;
	const parts = text.split(INLINE_MD).filter((p) => p !== undefined && p !== "");
	return (
		<>
			{parts.map((part, i) => {
				if (/^(\*\*|__)/.test(part)) {
					// Recurse. `INLINE_MD` lists bold first, so `**[label](href)**` matches the bold
					// alternative whole and the link never becomes its own part. Rendering `inner` as
					// a bare string then printed the link markup verbatim — which is what shipped:
					// every affiliate link inside a bold list item was literal text on the page.
					const inner = part.slice(2, -2);
					const body = <RichText text={inner} plain={plain} />;
					return plain ? <span key={i}>{body}</span> : <strong key={i}>{body}</strong>;
				}
				if (/^`/.test(part)) return <code key={i}>{part.slice(1, -1)}</code>;
				const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
				if (link) {
					const [, label, href] = link;
					const external = /^https?:\/\//.test(href) && !href.includes("stippl.io");
					// A route preview is an unprefixed app route, so it takes the page language as `?lang=`; anything else goes
					// through `localizeHref`, a no-op on absolute URLs, `mailto:` and anchors (see its doc comment).
					const localized = !external && isRoutePreviewHref(href) ? withRoutePreviewLang(href, lang) : localizeHref(href, lang);
					return (
						<a key={i} href={localized} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
							{label}
						</a>
					);
				}
				if (/^\*[^*]/.test(part) || /^_[^_]/.test(part)) {
					const inner = part.slice(1, -1);
					const body = <RichText text={inner} plain={plain} />;
					return plain ? <span key={i}>{body}</span> : <em key={i}>{body}</em>;
				}
				return <span key={i}>{part}</span>;
			})}
		</>
	);
}

export function Para({ children }: { children: React.ReactNode }) {
	if (!children) return null;
	// Only strings carry markdown; anything already assembled into elements is left alone.
	return <p className={styles.para}>{typeof children === "string" ? <RichText text={children} /> : children}</p>;
}

/**
 * Photographer credit for a curated photo.
 *
 * Both Unsplash and Pexels require the photographer named wherever the image appears, and
 * until now the guide pages showed none — the hero, the gallery and now the food and
 * highlight rows are all licensed images. Articles already credit this way
 * ("Photo by X via Y"), so this matches them rather than inventing a second convention.
 *
 * Not done here, and deliberately: Unsplash also asks for a ping to `download_location` when
 * an image is displayed. That is one network request per photo per page view on pages that
 * carry a dozen, so it is a performance decision rather than a rendering one. The field is
 * carried in the data and ready for whoever takes that call.
 */
export function PhotoCredit({ credit }: { credit?: MediaCredit }) {
	if (!credit?.author) return null;
	const source = credit.source ? credit.source.charAt(0).toUpperCase() + credit.source.slice(1) : null;
	return (
		<span className={styles.photoCredit}>
			Photo by{" "}
			{credit.author_url ? (
				<a href={credit.author_url} target="_blank" rel="noopener noreferrer nofollow">
					{credit.author}
				</a>
			) : (
				credit.author
			)}
			{source ? (
				<>
					{" via "}
					{credit.page_url ? (
						<a href={credit.page_url} target="_blank" rel="noopener noreferrer nofollow">
							{source}
						</a>
					) : (
						source
					)}
				</>
			) : null}
		</span>
	);
}

/**
 * Planning notes: undated claims the dossiers keep in their festivals block.
 *
 * They are not festivals — "the coasts have opposite monsoons", "northern burning season" —
 * and rendering them as festival rows gave each a bold title sliced mid-word out of its own
 * first sentence. They are not seasons either, so they are not cards: Chiang Mai alone has 22,
 * and a 22-card season grid trades a broken block for an unreadable one.
 *
 * A quiet list under the season cards, in the when-to-visit block where the ticket puts them.
 */
export function PlanningNotes({ notes }: { notes?: string[] }) {
	if (!notes?.length) return null;
	return (
		<ul className={styles.planningNotes}>
			{notes.map((n, i) => (
				<li key={`${n.slice(0, 32)}-${i}`}>
					<RichText text={n} />
				</li>
			))}
		</ul>
	);
}

export function Pill({ children, icon }: { children: React.ReactNode; icon?: React.ReactNode }) {
	return (
		<span className={styles.pill}>
			{icon}
			{children}
		</span>
	);
}

export function NumberedList({ items }: { items: { title: string; body?: string }[] }) {
	return (
		<div className={styles.numberedList}>
			{items.map((item, i) => (
				<div key={`${item.title}-${item.body ?? ""}-${i}`} className={styles.numberedListRow}>
					<span className={styles.numberedListIndex}>{String(i + 1).padStart(2, "0")}</span>
					<div className={styles.minW0}>
						{!!item.title && (
							<p className={styles.numberedListTitle}>
								<RichText text={item.title} />
							</p>
						)}
						{item.body && (
							<p className={styles.numberedListBody}>
								<RichText text={item.body} />
							</p>
						)}
					</div>
				</div>
			))}
		</div>
	);
}

export function LabelValueGrid({ items }: { items: { label: string; value: React.ReactNode }[] }) {
	if (!items.length) return null;
	return (
		<div className={styles.labelValueGrid}>
			{items.map((item) => (
				<div key={item.label} className={styles.labelValueCard}>
					<p className={styles.labelValueLabel}>{item.label}</p>
					<p className={styles.labelValueValue}>
						<RichText text={item.value} plain />
					</p>
				</div>
			))}
		</div>
	);
}

// Clicking the CTA either fires the caller's `onClick` (e.g. an "add to trip" flow that's
// already wired up) or, when omitted, falls straight through to the register page — saving
// isn't tied to real auth on these guide/article pages yet. With `href` the CTA is a plain link
// instead, for a block that knows exactly where to send the reader (the `route-cta` block's trip).
export function SaveWidget({
	icon,
	title,
	body,
	cta,
	tone = "primary",
	href,
	onClick,
}: {
	icon: GuideIconName;
	title: string;
	body?: string;
	cta: string;
	tone?: "primary" | "pink" | "purple" | "amber";
	href?: string;
	onClick?: () => void;
}) {
	const history = useHistory();
	const handleClick = () => {
		if (onClick) {
			onClick();
			return;
		}
		history.push("/register");
	};

	return (
		<div className={`${styles.saveWidget} ${styles[`saveWidgetBg-${tone}`]}`}>
			<span className={`${styles.iconCircle} ${styles.saveWidgetIcon} ${styles[`saveWidgetAccent-${tone}`]}`}>
				<GuideIcon name={icon} fill="white" size={20} />
			</span>
			<div className={`${styles.minW0} ${styles.flex1}`}>
				<p className={styles.saveWidgetTitle}>
					<RichText text={title} />
				</p>
				{body && (
					<p className={styles.saveWidgetBody}>
						<RichText text={body} />
					</p>
				)}
			</div>
			{href ? (
				<a href={href} className={`${styles.saveWidgetCta} ${styles[`saveWidgetAccent-${tone}`]}`}>
					{cta}
				</a>
			) : (
				<button type="button" onClick={handleClick} className={`${styles.saveWidgetCta} ${styles[`saveWidgetAccent-${tone}`]}`}>
					{cta}
				</button>
			)}
		</div>
	);
}

// ── Icon + colour lookups ────────────────────────────────────────────────────
const MICRO_ICONS: Record<string, { icon: GuideIconName; color: string; bg: string }> = {
	sun: { icon: "calendar", color: "#d98a12", bg: "#fef4e6" },
	bed: { icon: "bed", color: "#13c892", bg: "#e8f9f4" },
	budget: { icon: "budget", color: "#2e9e72", bg: "#e8f9f4" },
	route: { icon: "route", color: "#2f9bd6", bg: "#e9f4fb" },
	food: { icon: "eatdrink", color: "#e84d7e", bg: "#fdeef3" },
	map: { icon: "map", color: "#7c3aed", bg: "#f1ecfd" },
	temple: { icon: "heritage", color: "#9a5a10", bg: "#fde8c0" },
	culture: { icon: "culture", color: "#2f9bd6", bg: "#e9f4fb" },
	city: { icon: "city", color: "#1a7a5e", bg: "#d1f5ea" },
};

const FOR_WHO_ICONS: Record<string, { icon: GuideIconName; color: string; bg: string }> = {
	backpack: { icon: "packing", color: "#2e9e72", bg: "#e8f9f4" },
	food: { icon: "eatdrink", color: "#e84d7e", bg: "#fdeef3" },
	beach: { icon: "island", color: "#2f9bd6", bg: "#e9f4fb" },
	budget: { icon: "budget", color: "#d98a12", bg: "#fef4e6" },
	couple: { icon: "heart", color: "#e84d7e", bg: "#fdeef3" },
	culture: { icon: "culture", color: "#7c3aed", bg: "#f1ecfd" },
};

// Background is owned by the `kindBg-*` SCSS classes (keyed off the hyphenated `key` below),
// so KIND_CONFIG only needs the icon/text color, not a duplicate `bg` value.
const KIND_CONFIG: Record<string, { icon: GuideIconName; color: string; label: string }> = {
	city: { icon: "city", color: "#1a7a5e", label: "City" },
	capital: { icon: "city", color: "#1a7a5e", label: "Capital" },
	town: { icon: "town", color: "#b06a10", label: "Town" },
	"mountain town": { icon: "town", color: "#b06a10", label: "Town" },
	island: { icon: "island", color: "#1a6fa8", label: "Island" },
	coast: { icon: "island", color: "#1a6fa8", label: "Coast" },
	region: { icon: "region", color: "#5b28b5", label: "Region" },
	"natural feature": { icon: "naturalFeature", color: "#1a7a5e", label: "Natural feature" },
	waterfall: { icon: "naturalFeature", color: "#1a7a5e", label: "Natural feature" },
	"national park": { icon: "nationalPark", color: "#276635", label: "National park" },
	"ski resort": { icon: "skiResort", color: "#1a6fa8", label: "Ski resort" },
	ruins: { icon: "naturalFeature", color: "#1a7a5e", label: "Natural feature" },
	"heritage site": { icon: "heritage", color: "#9a5a10", label: "Heritage" },
	historic: { icon: "heritage", color: "#9a5a10", label: "Historic" },
};

// Data can arrive messy, e.g. kind: '"Natural feature"' (stray wrapping quotes) — trim,
// strip wrapping quotes and lowercase before looking the kind up in KIND_CONFIG.
const standardizeKind = (kind?: string | null) =>
	String(kind ?? "")
		.trim()
		.replace(/^["']+|["']+$/g, "")
		.trim()
		.toLowerCase();

/**
 * `{ icon, color, label, key }` for a destination `kind` (e.g. "Natural feature", "Island").
 * `key` is the normalised kind with spaces hyphenated — stable enough to group/filter on.
 * Unknown kinds fall back to the "city" config, matching `KindBadge` everywhere else.
 */
export const kindConfig = (kind: string) => {
	const normalized = standardizeKind(kind);
	const match = KIND_CONFIG[normalized] ? normalized : "city";
	return { ...KIND_CONFIG[match], key: match.replace(/\s+/g, "-") };
};

// Maps KIND_CONFIG's English `label` to the `guide.kind.<id>` dictionary key. KIND_CONFIG is a
// module-level constant and cannot call `useGuideUi` itself, so the translation lookup happens
// here at the one call site instead. Several KIND_CONFIG entries collapse onto the same badge
// label ("mountain town" and "town" both read "Town"; "waterfall"/"ruins"/"natural feature" all
// read "Natural feature"), so this keys off the label text rather than the raw record key.
const KIND_LABEL_KEYS: Record<string, string> = {
	City: "city",
	Capital: "capital",
	Town: "town",
	Island: "island",
	Coast: "coast",
	Region: "region",
	"Natural feature": "naturalFeature",
	"National park": "nationalPark",
	"Ski resort": "skiResort",
	Heritage: "heritage",
	Historic: "historic",
};

/** A destination kind's name in the page's language: "Town", "Natural feature" (the country map's place card and add sheet say it without the badge). */
export const kindLabel = (kind: string, ui: (key: string, fallback: string) => string): string => {
	const cfg = kindConfig(kind);
	return ui(`guide.kind.${KIND_LABEL_KEYS[cfg.label] ?? "city"}`, cfg.label);
};

export function KindBadge({ kind, dark = false, tilt }: { kind: string; dark?: boolean; tilt?: number }) {
	const cfg = kindConfig(kind);
	const ui = useGuideUi();
	const label = kindLabel(kind, ui);
	return (
		<span
			className={`${styles.kindBadge} ${dark ? styles.kindBadgeDark : styles[`kindBg-${cfg.key}`]}`}
			style={tilt ? { transform: `rotate(${tilt}deg)` } : undefined}
		>
			<span className={`${styles.iconCircle} ${styles.kindBadgeIcon} ${dark ? styles.kindBadgeIconDark : styles.kindBadgeIconLight}`}>
				<GuideIcon name={cfg.icon} size={dark ? 14 : 20} />
			</span>
			<span className={`${styles.kindBadgeLabel} ${dark ? styles.kindBadgeLabelDark : ""}`}>{label}</span>
		</span>
	);
}

const MONTH_COLORS: Record<MonthGuide["rating"], { bar: string; label: string }> = {
	best: { bar: "#13c892", label: "Best time" },
	good: { bar: "#7fd6ae", label: "Good" },
	ok: { bar: "#e8c96a", label: "Busy / hot" },
	poor: { bar: "#7db8e0", label: "Rainy / extreme" },
};

/**
 * The month strips index a colour map by `rating`, so a rating outside the union is an
 * `undefined.bar` that takes the whole page down with it — which is exactly what an
 * untranslated database vocabulary did to every country page. The resolver translates,
 * and this makes the failure a grey bar rather than a blank page if anything ever slips
 * past it again.
 */
const UNKNOWN_MONTH = { bar: "#d8dee6", label: "" };

/**
 * Localises a month abbreviation for the weather strips. `m.m` comes straight off Mongo as
 * the raw English three-letter form (`Jan`…`Dec`) — both strips below use this instead of
 * rendering it directly, keyed off the lowercased English value via `guide.chrome.month.<key>`
 * (see the seven blog locale dictionaries). `ui()`'s own fallback does the rest: an unrecognised
 * value never matches a `guide.chrome.month.*` key, so it falls back to the raw string rather
 * than going blank.
 */
const monthAbbr = (ui: (key: string, fallback: string) => string, m: string): string => ui(`guide.chrome.month.${m.slice(0, 3).toLowerCase()}`, m);

/**
 * The month bar's hover text. A rating outside the colour map has no label at all
 * (`UNKNOWN_MONTH.label` is the empty string), and joining that unconditionally published a
 * dangling separator — `"May — "` before, `"Mai: "` after. Neither is something to ship, so an
 * empty label leaves the month standing on its own.
 */
const monthTooltip = (month: string, label: string): string => (label ? `${month}: ${label}` : month);

/**
 * The twelve-month weather strip.
 *
 * Extracted from `WhenToGo` so a blog article can render it too — it is the
 * `weather-by-month` content block, and it was already built here, just not reachable
 * from anything but a city guide. The country page has its own near-identical copy
 * (`COUNTRY_MONTH_COLORS` below); unifying the two is worth doing but is a visual change
 * to a shipped page, so it is deliberately not bundled into the blog work.
 *
 * `highlight` is the month an article is *about* — "Thailand in November" dims the other
 * eleven rather than presenting twelve equal columns. Guides pass nothing and get the
 * plain strip they render today.
 *
 * `m` and `rating` are translated (`monthAbbr`/`ratingLabel` below, off `guide.chrome.month.*`
 * and `blog.section.weather.*`) because both are fixed vocabularies with a translation in all
 * seven locale files. The dossier's per-month `note` is free prose, not a vocabulary, is not
 * in `exploreTranslation.ts`'s allowlist (`guide.months` is excluded), and has never been paid
 * for in translation — see the comment on `WeatherBlock` in blockRegistry.tsx, which is why it
 * never reaches this component's props at all.
 */
export function WeatherByMonth({ months, highlight }: { months: MonthGuide[]; highlight?: string }) {
	const ui = useGuideUi();
	if (!months?.length) return null;
	const active = highlight?.slice(0, 3).toLowerCase();
	// English label stays the fallback; the key is `blog.section.weather.<rating>`.
	const ratingLabel = (k: MonthGuide["rating"]) => ui(`blog.section.weather.${k}`, MONTH_COLORS[k]?.label ?? "");
	return (
		<div className={styles.weatherCard}>
			<div className={styles.weatherHeader}>
				<span className={styles.weatherHeaderLabel}>{ui("blog.section.weatherByMonth", "Weather by month")}</span>
				<div className={styles.weatherLegend}>
					{(Object.keys(MONTH_COLORS) as MonthGuide["rating"][]).map((k) => (
						<span key={k} className={styles.weatherLegendItem}>
							<span className={`${styles.weatherLegendDot} ${styles[`monthDot-${k}`]}`} />
							<span className={styles.weatherLegendLabel}>{ratingLabel(k)}</span>
						</span>
					))}
				</div>
			</div>
			<div className={styles.weatherChart}>
				{months.map((m) => (
					<div key={m.m} className={`${styles.weatherMonth} ${active && m.m.slice(0, 3).toLowerCase() === active ? styles.weatherMonthActive : ""}`}>
						{m.temp && <span className={styles.weatherTemp}>{m.temp}</span>}
						<div
							className={`${styles.weatherBar} ${styles[`monthBar-${m.rating}`]}`}
							title={monthTooltip(monthAbbr(ui, m.m), MONTH_COLORS[m.rating] ? ratingLabel(m.rating) : UNKNOWN_MONTH.label)}
						/>
						<span className={styles.weatherMonthLabel}>{monthAbbr(ui, m.m)}</span>
					</div>
				))}
			</div>
		</div>
	);
}

// ── Scroll helpers ───────────────────────────────────────────────────────────
export function useScrollSpy(ids: string[], offset = 110) {
	const [active, setActive] = useState<string | null>(ids[0] ?? null);
	useEffect(() => {
		const onScroll = () => {
			let current: string | null = null;
			for (const id of ids) {
				const el = document.getElementById(id);
				if (el && el.getBoundingClientRect().top - offset <= 0) current = id;
			}
			setActive(current ?? ids[0] ?? null);
		};
		window.addEventListener("scroll", onScroll, { passive: true });
		onScroll();
		return () => window.removeEventListener("scroll", onScroll);
	}, [ids, offset]);
	return active;
}

export function scrollToSection(e: React.MouseEvent, id: string) {
	e.preventDefault();
	const el = document.getElementById(id);
	if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 96, behavior: "smooth" });
}

export function useDirectionalSticky(navOffset = 96, gap = 24) {
	const ref = useRef<HTMLDivElement>(null);
	const [top, setTop] = useState(navOffset);
	useEffect(() => {
		let lastY = window.scrollY;
		const onScroll = () => {
			const el = ref.current;
			if (!el) return;
			const h = el.offsetHeight;
			const vh = window.innerHeight;
			const y = window.scrollY;
			const goingDown = y > lastY;
			lastY = y;
			setTop(h <= vh - navOffset - gap ? navOffset : goingDown ? vh - h - gap : navOffset);
		};
		window.addEventListener("scroll", onScroll, { passive: true });
		onScroll();
		return () => window.removeEventListener("scroll", onScroll);
	}, [navOffset, gap]);
	return { ref, top };
}

// ── Hero "In this guide" topic grid — shared by both Hero variants ─────────
export type HeroTocItem = { id: string; label: string; desc: string; icon: GuideIconName; bg: string };
const HERO_TOC_TILTS = [-4, 3, -3, 4, -2, 3];

export function HeroTopics({ topics }: { topics: HeroTocItem[] }) {
	const ui = useGuideUi();
	if (!topics.length) return null;
	return (
		<div className={styles.heroTocWrap}>
			{/* Same English text, same key, as `blog.article.tocLabel` — the blog article sidebar's
			    "In this guide" label. Reused rather than duplicated into a new `guide.chrome.*`
			    key: it is the identical chrome string, already translated in all seven locales. */}
			<span className={styles.heroTocLabel}>{ui("blog.article.tocLabel", "In this guide")}</span>
			<div className={styles.heroTocGrid}>
				{topics.map((t, i) => (
					<a key={t.id} href={`#${t.id}`} onClick={(e) => scrollToSection(e, t.id)} className={styles.heroTocCard}>
						<span className={styles.heroTocIconWrap}>
							<span className={styles.heroTocIconBg} style={{ background: t.bg, transform: `rotate(${HERO_TOC_TILTS[i % HERO_TOC_TILTS.length]}deg)` }} />
							<span className={styles.heroTocIconInner}>
								<GuideIcon name={t.icon} fill="currentColor" size={16} />
							</span>
						</span>
						<div className={styles.heroTocCardText}>
							<p className={styles.heroTocCardTitle}>{t.label}</p>
							<p className={styles.heroTocCardDesc}>
								<RichText text={t.desc} />
							</p>
						</div>
						<svg
							width="13"
							height="13"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2.2"
							strokeLinecap="round"
							strokeLinejoin="round"
							className={styles.heroTocCardArrow}
						>
							<path d="M7 17L17 7M17 7H9M17 7v8" />
						</svg>
					</a>
				))}
			</div>
		</div>
	);
}

// ── Hero ─────────────────────────────────────────────────────────────────────
type HeroCommonProps = {
	name: string;
	tagline?: string;
	heroPhoto: string;
	mapEmbed: string;
	/** Cities for a country guide, curated places for a destination guide — see GuideMap. */
	mapMarkers?: GuideMapMarker[];
	/** `labelKey` is the untranslated lookup key, `label` the rendered prose — see `factValue` below. */
	quickFacts: { label: string; labelKey?: string; value: string }[];
	onPlan: () => void;
};

// A destination guide's hero (city/beach/etc. — links back to its parent country guide).
export type DestinationHeroProps = HeroCommonProps & {
	kind: string;
	country: string;
	displayName: string;
	displayCountry: string;
	stipplStats: { value: string; label: string }[];
	nutshell: { icon: string; text: string; strong: string }[];
	bestTime: string;
	neighbourhoodsCount: number;
	onOpenCountry: () => void;
	/** Country hub slug, e.g. `/discover/${countrySlug}` — the real href behind the breadcrumb below. */
	countrySlug: string;
};

// A country guide's hero — no parent to link back to, has its own download-app CTA instead.
export type CountryHeroProps = HeroCommonProps &
	Pick<CountryData, "countryCode" | "continent" | "bestTime" | "destinations"> & {
		/** the hub renders the route map (countryMap/): the hero gets the button that scrolls to it */
		routeMap?: boolean;
	};

export type HeroProps = DestinationHeroProps | CountryHeroProps;

// Real destination guides can carry a long tail of quick facts (subway passes, wards, safety, …) —
// show a first screenful and let the visitor expand for the rest instead of a wall of chips.
// Ilse, 2026-09-06: hide the destination hero's quick-facts strip until we decide whether it or
// the nutshell card should own those facts — see the comment at its render site. One line to
// restore, and nothing about the data changed.
const SHOW_DESTINATION_QUICK_FACTS = false;

// Food photos are gated in the data now, not here.
//
// They were off between 2026-09-07 (b4b2722d4) and this commit because the builder picked them
// with a signal that described the PLACE — a frame captioned "Kuala Lumpur" qualified as a
// verified char kway teow. A sample of 12 found 3 that were the dish and five that were not
// food at all.
//
// `subject_checked` now carries what a person confirmed is in the frame, and the builder only
// attaches a photo whose subject IS that dish, so an unverified photo cannot reach this
// component at all. Turning this back on is therefore a no-op today — the builder emits zero
// food photos — and correct tomorrow: imagery returns dish by dish as the review sheet
// (`explore/media/FOOD-PHOTO-REVIEW.md`) is applied, with no further frontend change.
//
// Left as one gate on purpose. Two gates is how the first one went unnoticed for a month.
const SHOW_FOOD_PHOTOS = true;

const QUICK_FACTS_VISIBLE = 4;

export function Hero(props: HeroProps) {
	const { name, tagline, heroPhoto, mapMarkers, quickFacts, onPlan } = props;
	// Narrow the union once, inline, so TS keeps each side's fields typed without casts.
	const country = "countryCode" in props ? props : undefined;
	const dest = "countryCode" in props ? undefined : props;
	const cfg = dest ? kindConfig(dest.kind) : null;
	// Destination guides prefer their curated nutshell; when it's empty (content gap in the
	// generated guide), the "in a nutshell" card falls back to the generic `facts` below instead
	// of disappearing.
	const useCuratedNutshell = !!dest && dest.nutshell.length > 0;
	const [showAllQuickFacts, setShowAllQuickFacts] = useState(false);
	const { appstoreUrl, handleTrack } = useAppStoreLink("discover");
	// guideRoutes.tsx doesn't thread `lang` down to this component — the SSR loader only passes
	// `match`/`initialData` — so this derives it the same way the homepage sections do.
	const lang = useMarketingLang();
	// NOT `lang` above: it falls back to "en" during SSR (no `window` yet), which would render
	// the AI search card's strings in English on the server and flip them on hydration.
	// `GuideUiContext` is provided by DiscoverCountryPage/DiscoverDestinationPage from their
	// route `lang` prop, which is correct on both sides.
	const ui = useGuideUi();

	// Looked up once and reused by both the topic tiles and the "in a nutshell" facts below —
	// a missing label just drops that tile/fact instead of rendering an empty/undefined value.
	// Markers stripped at the lookup rather than at each of the five interpolations below: these
	// values land inside the component's own `<strong>`, so any `**` in the dossier would either
	// print literally or nest bold inside bold. Both happened on the Thailand hero.
	// Matched on `labelKey` — the lower-cased ENGLISH label the knowledge base emits alongside the
	// prose one — never on `label` itself (123yb69326a).
	//
	// `guide.quickFacts[].label` is translated (graphql/helpers/exploreTranslation.ts), and this
	// used to join on it: `factValue("Daily budget")` against a row whose label on /es reads
	// "Presupuesto diario". Every such lookup missed, and because a missing fact is DROPPED rather
	// than rendered blank, the "in a nutshell" card silently lost rows — /es/discover/japan showed
	// three where /discover/japan shows six, with Budget, Pay in and the trip length gone entirely.
	// It read as a content gap in the Spanish dossier and was a join on prose.
	//
	// What made it survive review: "Capital" and "Visa" are spelled the same in Spanish, so the
	// card was never empty, just short. The same miss is why the "Daily budget" tile in the TOC
	// fell through to its "Budget tips" fallback on translated pages (123yb69326f).
	//
	// The `label` comparison is kept as a fallback, lower-cased on both sides, for documents synced
	// before the knowledge base started emitting `labelKey`. On an English document the two agree,
	// so it degrades to exactly today's behaviour rather than to nothing.
	const factValue = (key: string) => {
		const v = quickFacts.find((f) => (f.labelKey ?? f.label?.toLowerCase()) === key)?.value;
		return v === undefined ? undefined : stripInlineMd(v);
	};
	const dailyBudget = factValue("daily budget");
	const visa = factValue("visa");
	const currency = factValue("currency");
	const capital = factValue("capital");
	const idealLength = factValue("ideal length");
	const destinationsCount = country ? country.destinations.reduce((n, g) => n + g.items.length, 0) : 0;

	// Every `label` below is `guide.nav.<key>.label`; a `desc` gets its own `.desc` key only
	// where the English text is fixed chrome — a `desc` built from real data (`stripInlineMd`,
	// an interpolated count) stays as-is. Key names are independent of a tile's `id` (its DOM
	// anchor) on purpose: the country page's "highlights" anchor renders the "Not to miss" tile
	// (`notToMiss`), while the destination page's "highlights" anchor renders a differently
	// worded "Highlights" tile (`highlights`) — the two need distinct dictionary entries despite
	// sharing an anchor id, same reasoning as `useHeading`'s `dictId`.
	const topics: HeroTocItem[] = (
		country
			? [
					{
						id: "for-who",
						label: ui("guide.nav.forWho.label", "Who it's for"),
						desc: ui("guide.nav.forWho.desc", "Is this your trip?"),
						icon: "person",
						bg: "#e8f9f4",
					},
					country.bestTime && {
						id: "when",
						label: ui("guide.nav.when.label", "When to visit"),
						desc: stripInlineMd(country.bestTime),
						icon: "sun",
						bg: "#fef4e6",
					},
					{
						id: "festivals",
						label: ui("guide.nav.festivals.label", "Festivals"),
						desc: ui("guide.nav.festivals.desc", "Worth planning around"),
						icon: "specialdays",
						bg: "#f1ecfd",
					},
					destinationsCount > 0 && {
						id: "destinations",
						label: ui("guide.nav.destinations.label", "Destinations"),
						// {count} token, same pattern as `guide.chrome.seeMore` — a plain template string
						// shipped this English on all seven locales (123yb692m5g).
						desc: fillUi(ui("guide.chrome.destinationsCount", "{count} places"), { count: String(destinationsCount) }),
						icon: "destinations",
						bg: "#fdeef3",
					},
					{ id: "food", label: ui("guide.nav.food.label", "Food guide"), desc: ui("guide.nav.food.desc", "Eat like a local"), icon: "eatdrink", bg: "#fdeef3" },
					{
						id: "highlights",
						label: ui("guide.nav.notToMiss.label", "Not to miss"),
						desc: ui("guide.nav.notToMiss.desc", "Essential experiences"),
						icon: "heart",
						bg: "#f1ecfd",
					},
					{
						id: "culture",
						label: ui("guide.nav.culture.label", "Culture"),
						desc: ui("guide.nav.culture.desc", "Know before you go"),
						icon: "culture",
						bg: "#e9f4fb",
					},
					{
						id: "budget",
						label: ui("guide.nav.budget.label", "Daily budget"),
						desc: dailyBudget ?? ui("guide.chrome.budgetTips", "Budget tips"),
						icon: "budget",
						bg: "#e8f9f4",
					},
					{
						id: "practical",
						label: ui("guide.nav.practical.label", "Practicalities"),
						desc: ui("guide.nav.practical.desc", "Visa, SIM & more"),
						icon: "practicalities",
						bg: "#e8f0f2",
					},
				]
			: [
					{
						id: "highlights",
						label: ui("guide.nav.highlights.label", "Highlights"),
						desc: ui("guide.nav.highlights.desc", "Don't miss these"),
						icon: "heart",
						bg: "#f1ecfd",
					},
					dest!.bestTime && {
						id: "when",
						label: ui("guide.nav.whenCity.label", "When to go"),
						desc: stripInlineMd(dest!.bestTime),
						icon: "sun",
						bg: "#fef4e6",
					},
					dest!.neighbourhoodsCount > 0 && {
						id: "neighbourhoods",
						label: ui("guide.nav.neighbourhoods.label", "Neighbourhoods"),
						// {count} token, same pattern as `guide.chrome.seeMore` — see the destinations
						// tile above (123yb692m5g).
						desc: fillUi(ui("guide.chrome.neighbourhoodsCount", "{count} areas"), { count: String(dest!.neighbourhoodsCount) }),
						icon: "neighbourhood",
						bg: "#fdeef3",
					},
					{
						id: "stay",
						label: ui("guide.nav.stays.label", "Where to stay"),
						desc: ui("guide.nav.stays.desc", "Best areas & hotels"),
						icon: "bed",
						bg: "#e8f9f4",
					},
					{ id: "food", label: ui("guide.nav.eat.label", "What to eat"), desc: ui("guide.nav.eat.desc", "Eat like a local"), icon: "eatdrink", bg: "#fdeef3" },
					{
						id: "local-tips",
						label: ui("guide.nav.localTips.label", "Local tips"),
						desc: ui("guide.nav.localTips.desc", "Save time & money"),
						icon: "practicalities",
						bg: "#e8f0f2",
					},
				]
	).filter(Boolean) as HeroTocItem[];

	// Computed from quickFacts + bestTime for both hero types — a fact whose source value is
	// missing is dropped rather than rendered with a blank/undefined value. The destination
	// hero's card prefers its curated `nutshell` prop when it has entries (see below) and only
	// falls back to these generic facts when the guide has no curated nutshell.
	const heroBestTime = stripInlineMd(country ? country.bestTime : dest!.bestTime);
	// Each label is its own `guide.chrome.*` key — NOT `guide.routes.bestTime` (:2966-ish below),
	// which is the identical English string but scoped to the route card, translated separately
	// and reached by a different page state. Every value already went through `stripInlineMd`
	// above and sits inside its own `<strong>`, so only the label half needs a lookup
	// (123yb692m5g — these five were raw JSX literals with no dictionary tier at all).
	const facts: { icon: GuideIconName; color: string; bg: string; text: React.ReactNode }[] = [
		heroBestTime && {
			icon: "sun",
			color: "#d98a12",
			bg: "#fef4e6",
			text: (
				<>
					{ui("guide.chrome.bestTime", "Best time:")} <strong>{heroBestTime}</strong>
				</>
			),
		},
		dailyBudget && {
			icon: "budget",
			color: "#13c892",
			bg: "#e8f9f4",
			text: (
				<>
					{ui("guide.chrome.budgetFrom", "Budget from")} <strong>{dailyBudget}</strong>
				</>
			),
		},
		visa && {
			icon: "visum",
			color: "#0f3e4a",
			bg: "#e9f4fb",
			text: (
				<>
					{ui("guide.chrome.visa", "Visa:")} <strong>{visa}</strong>
				</>
			),
		},
		currency && {
			icon: "budget",
			color: "#7c3aed",
			bg: "#f1ecfd",
			text: (
				<>
					{ui("guide.chrome.payIn", "Pay in")} <strong>{currency}</strong>
				</>
			),
		},
		capital && {
			icon: "destinations",
			color: "#e84d7e",
			bg: "#fdeef3",
			text: (
				<>
					{ui("guide.chrome.startIn", "Start in")} <strong>{capital}</strong>
				</>
			),
		},
		idealLength && {
			icon: "calendar",
			color: "#13c892",
			bg: "#e8f9f4",
			text: (
				<>
					<strong>{idealLength}</strong>
				</>
			),
		},
	].filter(Boolean) as { icon: GuideIconName; color: string; bg: string; text: React.ReactNode }[];

	return (
		<section className={styles.hero}>
			<div className={styles.heroSDecoration} aria-hidden>
				<SPhotoWindow photo={heroPhoto} height={1220} rotate={-18} fadeColor="#003f4c" fadeSide="left" />
			</div>

			<div className={styles.heroInner}>
				<div className={styles.heroGrid}>
					{/* Left */}
					<div className={country ? styles.heroLeft : styles.minW0}>
						<div className={styles.heroLocationPill}>
							{country ? (
								<>
									<span className={`${styles.circleBadge} ${styles.heroLocationFlag}`}>
										<img src={flagUrl(country.countryCode)} alt={`${name} flag`} className={styles.coverImg} />
									</span>
									{country.continent && <span className={styles.heroLocationContinent}>{country.continent}</span>}
								</>
							) : (
								<>
									<CircleFlag country={dest!.country} size={18} />
									{/* Real href, not `#`: this crumb was the only link on a destination page up to
									    its country hub, and `#` meant not crawlable plus a dead middle-click,
									    cmd/ctrl-click and "open in new tab" — confirmed on 9 pages across different
									    countries, live on all ~170 (123yb692avr). onClick still does the SPA route
									    change on a plain left click; a modified click or a non-primary button falls
									    through to the browser instead of being swallowed by preventDefault. */}
									<a
										href={discoverPath(`/discover/${dest!.countrySlug}`, lang)}
										onClick={(e) => {
											if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
											e.preventDefault();
											dest!.onOpenCountry();
										}}
										className={styles.heroLocationLink}
									>
										{dest!.displayCountry}
									</a>
								</>
							)}
							<svg width="5" height="8" viewBox="0 0 6 10" fill="none" aria-hidden className={country ? styles.heroLocationChevron : styles.heroChevron}>
								<path d="M1 1l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
							<span className={styles.heroLocationName}>{country ? name : dest!.displayName}</span>
						</div>

						{country ? (
							<h1 className={styles.countryHeroTitle}>{name}</h1>
						) : (
							<div className={styles.heroTitleWrap}>
								<div className={styles.heroKindBadgeWrap}>
									<KindBadge kind={dest!.kind} dark />
								</div>
								<div className={styles.heroTitleRow}>
									<h1 className={styles.heroTitle}>{dest!.displayName}</h1>
									{dest!.stipplStats[0] && (
										<div className={styles.heroStatPill}>
											<span className={styles.heroFireEmoji}>🔥</span>
											<span className={styles.heroStatText}>
												<span className={styles.heroStatValue}>{dest!.stipplStats[0].value}</span> {dest!.stipplStats[0].label}
											</span>
										</div>
									)}
								</div>
							</div>
						)}

						{tagline && (
							<p className={styles.heroTagline}>
								<RichText text={tagline} />
							</p>
						)}

						<div className={styles.heroSearchWrap}>
							<HeroSearchCard
								variant="plan"
								subtitle={
									country
										? fillUi(ui("blog.hero.planSubtitleCountry", "your {name} trip with AI"), { name })
										: fillUi(ui("blog.hero.planSubtitleCity", "your {name} days with AI"), { name })
								}
								// Deliberately NOT `blog.section.aiPrompt1/2/3` below, even though three of the
								// country prompts read the same in English: those keys serve both countries
								// and cities from one string, and fr/it need a different preposition for each
								// ("3 semaines à Bangkok" but "en Thaïlande") — a shared key is wrong in at
								// least two locales. Separate country/city keys are what make the translations
								// in the seven dictionary files correct. Do not fold these back into aiPrompt*.
								// Trailing "..." is appended here, not stored in the dictionary, so every
								// prompt stays consistent and the dictionary values stay clean.
								planPrompts={
									country
										? [
												`${fillUi(ui("blog.hero.planPromptCountry1", "3 weeks in {name}, islands and food"), { name })}...`,
												`${fillUi(ui("blog.hero.planPromptCountry2", "First time in {name}, 14 days"), { name })}...`,
												`${fillUi(ui("blog.hero.planPromptCountry3", "{name} with kids, no long bus rides"), { name })}...`,
												`${fillUi(ui("blog.hero.planPromptCountry4", "Backpacking {name} on a budget"), { name })}...`,
												`${fillUi(ui("blog.hero.planPromptCountry5", "{name} honeymoon, beaches and culture"), { name })}...`,
											]
										: [
												`${fillUi(ui("blog.hero.planPromptCity1", "3 days in {name}, temples and street food"), { name })}...`,
												`${fillUi(ui("blog.hero.planPromptCity2", "{name} layover, 24 hours"), { name })}...`,
												`${fillUi(ui("blog.hero.planPromptCity3", "First time in {name}, where to stay"), { name })}...`,
												`${fillUi(ui("blog.hero.planPromptCity4", "{name} and then the islands, 2 weeks"), { name })}...`,
											]
								}
								onSubmit={onPlan}
							/>
						</div>

						{country && (
							<div className={styles.heroDownloadRow}>
								<a href={appstoreUrl} onClick={handleTrack} target="_blank" rel="noreferrer" className={styles.heroDownloadBtn}>
									<span className={styles.heroDownloadIcon}>
										<StipplS size={18} />
									</span>
									{ui("guide.chrome.downloadApp", "Download the app")}
								</a>
								<span className={styles.heroDownloadNote}>{ui("guide.chrome.downloadNote", "Free · iOS & Android")}</span>
							</div>
						)}

						{/* Quick facts strip — destination hero only.
						    HIDDEN 2026-09-06 at Ilse's request while reviewing the first hand-written
						    destination pages: on a destination hero this strip sits directly above the
						    nutshell card and repeats most of it. Kyoto shows Best time / Ideal length /
						    Daily budget / Currency here, and Best from / Stay / From / Move by on the
						    card — four of six saying the same thing twice, a few pixels apart.
						    Hidden rather than deleted because which of the two survives is a content
						    decision nobody has made yet: the card is curated per geo (`nutshell`), the
						    strip is the raw `quickFacts` list, and the strip carries four facts the card
						    does not. Both still ship in the payload, so flipping this back is one line.
						    Country heroes are unaffected — they never rendered this strip. */}
						{SHOW_DESTINATION_QUICK_FACTS && dest && quickFacts.length > 0 && (
							<div className={styles.heroQuickFactsWrap}>
								<div className={styles.heroQuickFacts}>
									{(showAllQuickFacts ? quickFacts : quickFacts.slice(0, QUICK_FACTS_VISIBLE)).map((f) => (
										<span key={f.label} className={styles.heroQuickFact}>
											<span className={styles.heroQuickFactLabel}>{stripInlineMd(f.label)}</span>
											<span className={styles.heroQuickFactValue}>
												<RichText text={f.value} plain />
											</span>
										</span>
									))}
								</div>
								{quickFacts.length > QUICK_FACTS_VISIBLE && (
									<button type="button" id="hero-quickfacts-toggle" className={styles.heroQuickFactsToggle} onClick={() => setShowAllQuickFacts((v) => !v)}>
										{showAllQuickFacts
											? ui("guide.chrome.showLess", "Show less")
											: fillUi(ui("guide.chrome.seeMore", "See {count} more"), { count: String(quickFacts.length - QUICK_FACTS_VISIBLE) })}
									</button>
								)}
							</div>
						)}

						{/* Topic tiles */}
						<HeroTopics topics={topics} />
					</div>

					{/* Right: tilted map + facts/nutshell card */}
					<div className={styles.heroRight}>
						<div className={styles.heroMapTilt}>
							<div className={country ? styles.countryHeroMapCard : `${styles.heroMapCard} ${styles.mapBg}`}>
								<div className={styles.absoluteFillImg}>
									<LazyGuideMap id={`hero-${country ? country.countryCode : "dest"}`} markers={mapMarkers || []} countryName={country ? name : undefined} />
								</div>
								{country ? (
									<div className={styles.countryHeroMapBadge}>
										<span className={`${styles.circleBadge} ${styles.heroMapBadgeFlag}`}>
											<img src={flagUrl(country.countryCode)} alt="" className={styles.coverImg} />
										</span>
										<span className={styles.heroMapBadgeLabel}>{name}</span>
									</div>
								) : (
									<div className={styles.heroMapBadgeWrap}>
										<span className={styles.heroMapBadge}>
											<GuideIcon name={cfg!.icon} fill={cfg!.color} size={14} />
											<span className={styles.heroMapBadgeLabel}>{name}</span>
										</span>
									</div>
								)}
							</div>
						</div>

						{country?.routeMap && (
							<button
								type="button"
								className={styles.heroRouteBtn}
								onClick={() => {
									openRouteMapTab("routes");
									scrollToRouteMap();
								}}
							>
								<GuideIcon name="route" fill="currentColor" size={18} />
								{ui("guide.routeMap.heroCta", "Plan your route on the map")}
							</button>
						)}

						{(useCuratedNutshell || facts.length > 0) && (
							<div className={country ? styles.heroFactsCard : styles.heroNutshellCard}>
								<div className={country ? styles.heroFactsHeader : styles.heroNutshellHeader}>
									{country ? (
										<span className={`${styles.circleBadge} ${styles.heroFactsFlagBadge}`}>
											<img src={flagUrl(country.countryCode)} alt={`${name} flag`} className={styles.coverImg} />
										</span>
									) : (
										<span className={`${styles.iconCircle} ${styles.heroNutshellIcon} ${styles[`kindBg-${cfg!.key}`]}`}>
											<GuideIcon name={cfg!.icon} fill={cfg!.color} size={16} />
										</span>
									)}
									<p className={country ? styles.heroFactsTitle : styles.heroNutshellTitle}>
										{fillUi(ui("guide.chrome.nutshellTitle", "{name} in a nutshell"), { name })}
									</p>
								</div>
								<div className={country ? styles.heroFactsList : styles.heroNutshellList}>
									{!useCuratedNutshell
										? facts.map((f, i) => (
												<div key={i} className={styles.heroFactItem}>
													<span
														className={`${styles.iconCircle} ${styles.heroFactIcon}`}
														style={{ background: f.bg, color: f.color, transform: `rotate(${[-4, 3, -3, 4, -3, 3][i]}deg)` }}
													>
														<GuideIcon name={f.icon} fill={f.color} size={18} />
													</span>
													<span className={styles.heroFactItemText}>
														<RichText text={f.text} />
													</span>
												</div>
											))
										: dest!.nutshell.map((f, i) => {
												const ic = MICRO_ICONS[f.icon] ?? MICRO_ICONS.map;
												const iconKey = MICRO_ICONS[f.icon] ? f.icon : "map";
												return (
													<div key={f.text} className={styles.heroNutshellRow}>
														<span
															className={`${styles.iconCircle} ${styles.heroNutshellIconWrap} ${styles[`microBg-${iconKey}`]} ${NUTSHELL_TILTS[i % NUTSHELL_TILTS.length]}`}
														>
															<GuideIcon name={ic.icon} fill={ic.color} size={17} />
														</span>
														<span className={styles.heroNutshellText}>
															<RichText text={f.text} /> <strong className={styles.heroNutshellStrong}>{f.strong}</strong>
														</span>
													</div>
												);
											})}
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</section>
	);
}

// ── Mobile map ───────────────────────────────────────────────────────────────
export function MobileMap({ guide }: { guide: { name: string; mapEmbed: string; mapMarkers?: GuideMapMarker[]; isCountry?: boolean } }) {
	const ui = useGuideUi();
	return (
		<div className={styles.mobileMap}>
			<div className={`${styles.relativeFull} ${styles.mapBg} ${styles.aspect43}`}>
				<LazyGuideMap id="mobile" markers={guide.mapMarkers || []} countryName={guide.isCountry ? guide.name : undefined} />
			</div>
			<div className={styles.mobileMapFooter}>
				<GuideIcon name="map" fill="currentColor" size={15} />
				<span className={styles.mobileMapLabel}>{ui("guide.chrome.everySpotOnMap", "Every spot in this guide, on one map")}</span>
			</div>
		</div>
	);
}

// ── Highlights ───────────────────────────────────────────────────────────────
export function Highlights({ guide }: { guide: CityData }) {
	if (!guide.highlights.length) return null;
	const photos = guide.highlights.filter((h) => h.photo).slice(0, 3);
	return (
		<>
			<SectionHeading id="highlights" kicker="Highlights" icon="heart" tilt={4} vars={{ name: guide.name }}>
				What not to miss in {guide.name}
			</SectionHeading>
			{photos.length > 0 && (
				<div className={styles.highlightsPhotos}>
					{photos.map((h) => (
						<figure key={h.photo} className={styles.highlightsPhoto}>
							{/* KB alt describes the frame; title/body are the pre-123yb6937nz fallback for a
							    guide synced before the rewrite. */}
							<img
								src={sizedPhoto(h.photo, 720)}
								srcSet={srcSetFor(h.photo, 720) || undefined}
								alt={h.photoCredit?.alt || h.title || h.body}
								loading="lazy"
								className={styles.coverImg}
							/>
							<PhotoCredit credit={h.photoCredit} />
						</figure>
					))}
				</div>
			)}
			<NumberedList items={guide.highlights} />
		</>
	);
}

// ── For who ──────────────────────────────────────────────────────────────────
// ── When to go ───────────────────────────────────────────────────────────────
export function WhenToGo({ guide, onPlan }: { guide: CityData; onPlan: () => void }) {
	// Above the early return, not below it — see the identical comment on `WhenToVisit` below.
	const ui = useGuideUi();
	if (!guide.bestTime && !guide.whenToGo && !guide.months.length && !guide.seasons.length) return null;
	return (
		<>
			<SectionHeading id="when" kicker="Timing" icon="calendar" tilt={3} vars={{ name: guide.name }}>
				When to go to {guide.name}
			</SectionHeading>
			{guide.bestTime && (
				<div className={styles.whenBestTimePill}>
					<GuideIcon name="calendar" fill="currentColor" size={16} />
					<span className={styles.whenBestTimeText}>
						{ui("guide.chrome.bestTime", "Best time:")} <RichText text={guide.bestTime} />
					</span>
				</div>
			)}
			{guide.whenToGo && <Para>{guide.whenToGo}</Para>}

			<WeatherByMonth months={guide.months} />

			{guide.seasons.length > 0 && (
				<div className={styles.seasonsGrid}>
					{guide.seasons.map((s, i) => {
						return (
							<div key={`${s.name}-${s.period}-${i}`} className={styles.seasonCard}>
								{!!s.name && <p className={styles.seasonName}>{s.name}</p>}
								<p className={`${styles.seasonPeriod} ${SEASON_TONE_CLASSES[i % SEASON_TONE_CLASSES.length]}`}>{s.period}</p>
								<p className={styles.seasonBody}>
									<RichText text={s.body} />
								</p>
							</div>
						);
					})}
				</div>
			)}
			<PlanningNotes notes={guide.planningNotes} />
			<SaveWidget
				icon="calendar"
				title={fillUi(ui("guide.widget.whenCity.title", "Add your {name} dates and we'll flag the weather"), { name: guide.name })}
				body={ui("guide.widget.whenCity.body", "Stippl shows the season, crowds and prices for every stop on your route.")}
				cta={ui("guide.widget.whenCity.cta", "Start planning")}
				onClick={onPlan}
			/>
		</>
	);
}

// ── Neighbourhoods ───────────────────────────────────────────────────────────
export function Neighbourhoods({ guide, onPlan }: { guide: CityData; onPlan: () => void }) {
	const ui = useGuideUi();
	if (!guide.neighbourhoods.length) return null;
	return (
		<>
			<SectionHeading id="neighbourhoods" kicker="Area by area" icon="neighbourhood" tilt={-3} vars={{ name: guide.name }}>
				The neighbourhoods of {guide.name}
			</SectionHeading>
			<div className={styles.neighbourhoodsCard}>
				<div className={styles.neighbourhoodsHeaderRow}>
					{/* `Why go` is a new dossier column, so it is empty until content fills it.
					    A header over a column with nothing under it reads as a rendering bug. */}
					{/* Through `ui()` like every other label on this page. These three were plain string
					    literals in an inline array, so the table shipped English column headers over
					    translated rows on all six non-English locales — "NEIGHBOURHOOD / WHAT IT'S LIKE /
					    WHY VISIT" above French prose. An array literal is easy to miss in an i18n sweep
					    precisely because it does not look like copy. */}
					{[
						ui("guide.section.neighbourhoods.colName", "Neighbourhood"),
						ui("guide.section.neighbourhoods.colWhat", "What it's like"),
						...(guide.neighbourhoods.some((n) => n.whyVisit) ? [ui("guide.section.neighbourhoods.colWhy", "Why visit")] : []),
					].map((h) => (
						<span key={h} className={styles.neighbourhoodsHeaderCell}>
							{h}
						</span>
					))}
				</div>
				{guide.neighbourhoods.map((n, i) => (
					<div key={n.name} className={`${styles.neighbourhoodsRow} ${i % 2 ? styles.neighbourhoodsRowAlt : ""}`}>
						<div className={styles.neighbourhoodsNameCol}>
							<span className={`${styles.iconCircle} ${styles.neighbourhoodsIcon} ${NEIGHBOURHOOD_TILTS[i % NEIGHBOURHOOD_TILTS.length]}`}>
								<GuideIcon name="neighbourhood" fill="#e84d7e" size={13} />
							</span>
							<div className={styles.minW0}>
								<p className={styles.neighbourhoodsName}>{stripInlineMd(n.name)}</p>
								{n.vibe && (
									<p className={styles.neighbourhoodsVibe}>
										<RichText text={n.vibe} />
									</p>
								)}
							</div>
						</div>
						<p className={styles.neighbourhoodsBody}>
							<RichText text={n.body} />
						</p>
						{!!n.whyVisit && (
							<div className={styles.neighbourhoodsWhyCol}>
								<span className={styles.neighbourhoodsWhyLabel}>Why:&nbsp;</span>
								<p className={styles.neighbourhoodsWhyText}>{n.whyVisit}</p>
							</div>
						)}
					</div>
				))}
			</div>
			<SaveWidget
				icon="map"
				title={fillUi(ui("guide.widget.neighbourhoods.title", "See every {name} neighbourhood on one map"), { name: guide.name })}
				body={ui("guide.widget.neighbourhoods.body", "Save the areas you like and Stippl plots your days so you're not crossing the city twice.")}
				cta={ui("guide.widget.neighbourhoods.cta", "Open the map")}
				tone="pink"
				onClick={onPlan}
			/>
		</>
	);
}

// ── Where to stay ────────────────────────────────────────────────────────────
export function WhereToStay({ guide }: { guide: CityData }) {
	const ui = useGuideUi();
	if (!guide.stays.intro && !guide.stays.areas.length && !guide.stays.hotels.length) return null;
	return (
		<>
			<SectionHeading id="stay" kicker={ui("blog.section.stayKicker", "Sleep well")} icon="bed" tilt={4}>
				{fillUi(ui("blog.section.stayHeading", "Where to stay in {name}"), { name: guide.name })}
			</SectionHeading>
			{guide.stays.intro && <Para>{guide.stays.intro}</Para>}

			{guide.stays.areas.length > 0 && (
				<div className={styles.stayGrid}>
					{guide.stays.areas.map((a, i) => (
						<div key={a.name} className={styles.stayItem}>
							<div className={styles.stayHeader}>
								<span className={`${styles.iconCircle} ${styles.stayIcon} ${STAY_TILTS[i % STAY_TILTS.length]}`}>
									<GuideIcon name="bed" fill="currentColor" size={17} />
								</span>
								<div className={styles.minW0}>
									<p className={styles.stayName}>{stripInlineMd(a.name)}</p>
									{/* `Price` is a new dossier column, empty until content fills it — and empty
									    for any area whose sources give no figure, which stays empty on purpose.
									    Rendering the <p> regardless put six blank price lines on every city page. */}
									{!!a.price && <p className={styles.stayPrice}>{stripInlineMd(a.price)}</p>}
								</div>
							</div>
							<p className={styles.stayBody}>
								<RichText text={a.body} />
							</p>
						</div>
					))}
				</div>
			)}

			<StayWidget guide={guide} />
		</>
	);
}

/**
 * The booking half of "Where to stay": the search box with the date picker, up to three named
 * hotel picks, then Booking.com and Hostelworld — without the "Where to stay in {name}" heading
 * or the area grid `WhereToStay` wraps it in.
 *
 * Split out for `hotel-picks` (`blockRegistry.tsx`, 123yb693pet): that block sits inside an
 * article section which already has its own heading, so rendering `WhereToStay` whole there
 * would duplicate the article's own structure. `WhereToStay` above renders through this with
 * nothing else supplied — identical markup to before the split.
 *
 * `hotels` defaults to the whole guide's curated picks; `hotel-picks` with an `area` prop
 * narrows it to one neighbourhood before calling in — see `hotelsInArea` in `blockRegistry.tsx`.
 */
export function StayWidget({ guide, hotels }: { guide: CityData; hotels?: CityData["stays"]["hotels"] }) {
	const ui = useGuideUi();
	const picks = hotels ?? guide.stays.hotels;
	// Not gated on `hotels.length` any more.
	// The widget is two things: named picks, and the search box with the date
	// picker, the Booking.com and Hostelworld buttons and the "see all hotels" panel.
	// Only the picks need hotel data — `TravelWidget` already hides that list on its
	// own when there is none — but the gate here took the booking half down with it,
	// which left the destination pages with no affiliate path at all (86cba1hzn).
	// The picks are live now: the Nuitee backfill filled `photo` on 321 hotels across
	// 124 geos, so the named cards render wherever the KB has them. Geos with no
	// curated stays still get the booking half on its own. `hotel-picks` scoped to an area
	// leans on this same fallback: most areas have no matching hotel (Bangkok curates 3
	// across 6), and an empty `hotels` array here is exactly that case, not an error.
	return (
		<div className={styles.maxW720}>
			<TravelWidget
				variant="hotels"
				destination={guide.name}
				country={guide.country}
				// Seeds the in-app stay search the primary CTA / "See all" now open.
				lat={guide.lat}
				lng={guide.lng}
				title={fillUi(ui("blog.section.stayWidgetTitle", "Our favourite places to stay in {name}"), { name: guide.name })}
				hotels={picks}
				featurePhoto={picks[0]?.photo ?? guide.heroPhoto}
				featureLabel={fillUi(ui("blog.section.staySeeAll", "See all\nhotels in {name}"), { name: guide.name })}
			/>
		</div>
	);
}

// ── Food ─────────────────────────────────────────────────────────────────────
// ── Fun facts ────────────────────────────────────────────────────────────────
export function FunFacts({ guide }: { guide: CityData }) {
	if (!guide.funFacts.length) return null;
	return (
		<>
			<SectionHeading id="fun-facts" kicker="Trivia" icon="culture" tilt={3} vars={{ name: guide.name }}>
				Fun facts about {guide.name}
			</SectionHeading>
			<div className={styles.funFactsGrid}>
				{guide.funFacts.map((f, i) => (
					<div key={f.slice(0, 30)} className={styles.funFactCard}>
						<span className={`${styles.funFactIndex} ${NUTSHELL_TILTS[i % NUTSHELL_TILTS.length]}`}>{String(i + 1).padStart(2, "0")}</span>
						<p className={styles.funFactText}>
							<RichText text={f} />
						</p>
					</div>
				))}
			</div>
		</>
	);
}

// ── Local tips ───────────────────────────────────────────────────────────────
export function LocalTips({ guide }: { guide: CityData }) {
	if (!guide.localTips.length) return null;
	return (
		<>
			<SectionHeading id="local-tips" kicker="From people who live there" icon="practicalities" tilt={-3} vars={{ name: guide.name }}>
				Local tips for {guide.name}
			</SectionHeading>
			<div className={styles.localTipsList}>
				{guide.localTips.map((t, i) => {
					const ic = MICRO_ICONS[t.icon] ?? MICRO_ICONS.map;
					const iconKey = MICRO_ICONS[t.icon] ? t.icon : "map";
					return (
						<div key={`${t.title}-${i}`} className={`${styles.localTipsRow} ${i % 2 ? styles.localTipsRowAlt : ""}`}>
							<span
								className={`${styles.iconCircle} ${styles.localTipsRowIcon} ${styles[`microBg-${iconKey}`]} ${LOCAL_TIPS_TILTS[i % LOCAL_TIPS_TILTS.length]}`}
							>
								<GuideIcon name={ic.icon} fill={ic.color} size={19} />
							</span>
							<div className={styles.minW0}>
								{!!t.title && <p className={styles.localTipsRowTitle}>{t.title}</p>}
								<p className={styles.localTipsRowBody}>
									<RichText text={t.body} />
								</p>
							</div>
						</div>
					);
				})}
			</div>
		</>
	);
}

// ── FAQ ──────────────────────────────────────────────────────────────────────
export function Faq({
	guide,
	kicker = "Everything else",
	title,
	isCountry = false,
}: {
	guide: { name: string; faqs: { q: string; a: string }[] };
	/** Eyebrow above the heading. The blog article pages pass a localised string; guides keep the default. */
	kicker?: string;
	/** Full heading. When omitted, falls back to "Frequently asked questions about {name}". */
	title?: React.ReactNode;
	/**
	 * True from `DiscoverCountryPage` only. `Faq` is one component shared by both guide pages,
	 * but "about {name}" is governed by a preposition that needs the definite article for a
	 * country name in fr/it/pt ("sur le Japon", not "sur Japon") and doesn't for a city ("sur
	 * Bangkok") — the same trap as `Food`/`Budget`/`NotToMiss` below. Picking any one fixed
	 * article would still be wrong for roughly two countries in three (gender/number vary), so
	 * those three locales restructure the H2 to avoid the preposition entirely under a distinct
	 * `guide.section.faqCountry.h2` key instead. Defaults to the destination form so nothing
	 * about the (much more common) destination render changes.
	 */
	isCountry?: boolean;
}) {
	const [open, setOpen] = useState<number | null>(0);
	if (!guide.faqs.length) return null;
	return (
		<>
			{/* `useDictionary={title === undefined}`: a blog article passes `title` already resolved
			    from its own `blog.article.faqTitle` key, with the real name filled in. The guide
			    pages pass nothing and want `guide.section.faq.h2` (or `faqCountry.h2`) to do that
			    job instead. Letting the dictionary tier run unconditionally here would have let it
			    silently replace an article's already-correct title with an unfilled "{name}"
			    literal — the tier has no `vars` to fill it back in on this call. */}
			<SectionHeading
				id="faq"
				kicker={kicker}
				icon="faq"
				tilt={4}
				vars={{ name: guide.name }}
				useDictionary={title === undefined}
				dictId={isCountry ? "faqCountry" : undefined}
			>
				{title ?? `Frequently asked questions about ${guide.name}`}
			</SectionHeading>
			<div className={styles.faqList}>
				{guide.faqs.map((f, i) => {
					const isOpen = open === i;
					return (
						<div key={f.q} className={`${styles.faqItem} ${isOpen ? styles.faqItemOpen : ""}`}>
							<button type="button" onClick={() => setOpen(isOpen ? null : i)} className={styles.faqQuestion}>
								<span className={styles.faqQuestionText}>{f.q}</span>
								<span className={`${styles.faqChevron} ${isOpen ? styles.faqChevronOpen : ""}`}>
									<svg
										width="15"
										height="15"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<path d="M6 9l6 6 6-6" />
									</svg>
								</span>
							</button>
							<div className={`${styles.faqAnswerWrap} ${isOpen ? styles.faqAnswerWrapOpen : ""}`}>
								<div className={styles.overflowHidden}>
									<p className={styles.faqAnswer}>
										<RichText text={f.a} />
									</p>
								</div>
							</div>
						</div>
					);
				})}
			</div>
		</>
	);
}

// ── AI plan banner ───────────────────────────────────────────────────────────
// ── Inline "read more" strip + related articles ──────────────────────────────

export type ArticleCard = { title: string; tag: string; photo: string; read: string; href?: string; onClick?: () => void };

export function InlineReadMore({
	articles,
	label = "Also worth reading",
	buildHref,
}: {
	articles: ArticleCard[];
	label?: string;
	/** Builds the article's URL (e.g. into /blog/destinations/...). Omit to keep cards decorative. */
	buildHref?: (article: ArticleCard) => string;
}) {
	const history = useHistory();
	if (!articles.length) return null;
	return (
		<div className={styles.readMoreWrap}>
			<span className={styles.readMoreLabel}>{label}</span>
			<div className={styles.readMoreGrid}>
				{articles.map((a) => {
					const tagClass = styles[`tag${a.tag}`] ?? styles.tagDefault;
					const href = buildHref?.(a) ?? "#";
					return (
						<a
							key={a.title}
							href={href}
							onClick={(e) => {
								e.preventDefault();
								a.onClick?.();
								if (buildHref) history.push(href);
							}}
							className={styles.readMoreCard}
						>
							<div className={styles.readMoreImgWrap}>
								<img src={sizedPhoto(a.photo, 360)} srcSet={srcSetFor(a.photo, 360) || undefined} alt={a.title} loading="lazy" className={styles.readMoreImg} />
							</div>
							<div className={styles.readMoreBody}>
								<span className={styles.readMoreTagRow}>
									<span className={`${styles.readMoreTag} ${tagClass}`}>{a.tag}</span>
									<span className={styles.readMoreRead}>{a.read}</span>
								</span>
								<p className={styles.readMoreTitle}>{a.title}</p>
							</div>
						</a>
					);
				})}
			</div>
		</div>
	);
}

export function RelatedArticles({
	heading,
	articles,
	action,
	buildHref,
	kicker = "Read on",
	readLabel = "read",
}: {
	heading: React.ReactNode;
	articles: ArticleCard[];
	action?: React.ReactNode;
	/** Builds the article's URL (e.g. into /blog/destinations/...). Omit to keep cards decorative. */
	buildHref?: (article: ArticleCard) => string;
	/** Eyebrow above the heading. The blog article pages pass a localised string; guides keep the default. */
	kicker?: string;
	/** Trailing word on each card's reading-time chip ("8 min read"). Localised by the blog article pages. */
	readLabel?: string;
}) {
	const history = useHistory();
	// `heading` is still the caller's default — the override only wins when a copy file names
	// `related`. This component is also used from the blog article page, which passes no
	// override and is not inside the provider, so it keeps rendering exactly as before.
	const relatedHeading = useHeading("related", kicker, heading);
	if (!articles.length) return null;
	return (
		<section className={styles.relatedSection}>
			<div className={styles.relatedInner}>
				<div className={styles.relatedHeader}>
					<div>
						<span className={styles.relatedKicker}>{relatedHeading.kicker}</span>
						<h2 className={styles.relatedTitle}>{relatedHeading.h2}</h2>
					</div>
					{action}
				</div>
				<div className={styles.relatedGrid}>
					{articles.map((a) => {
						// `a.href` as well as `buildHref`: the guide pages pass a builder, but an
						// article passes the URL on the card itself — and this only read the
						// builder, so those cards rendered with `href="#"` and a preventDefault
						// that stopped them navigating at all.
						const href = buildHref?.(a) ?? a.href ?? "#";
						const navigable = href !== "#";
						return (
							<a
								key={a.title}
								href={href}
								onClick={(e) => {
									e.preventDefault();
									a.onClick?.();
									if (navigable) history.push(href);
								}}
								className={styles.relatedCard}
							>
								<img
									src={sizedPhoto(a.photo, 350)}
									srcSet={srcSetFor(a.photo, 350) || undefined}
									alt={a.title}
									loading="lazy"
									className={styles.relatedCardImg}
								/>
								<div className={`${styles.absoluteFillImg} ${styles.relatedCardGradient}`} />
								<div className={styles.relatedCardFooter}>
									<div className={styles.relatedCardTagRow}>
										<span className={styles.relatedCardTag}>{a.tag}</span>
										<span className={styles.relatedCardRead}>
											{a.read} {readLabel}
										</span>
									</div>
									<p className={styles.relatedCardTitle}>{a.title}</p>
								</div>
							</a>
						);
					})}
				</div>
			</div>
		</section>
	);
}

// ── Bottom download banner ───────────────────────────────────────────────────
export function DownloadBanner({ guide }: { guide: CityData }) {
	const { appstoreUrl, handleTrack } = useAppStoreLink("discover");
	const ui = useGuideUi();
	return (
		<section className={styles.downloadSection}>
			<div className={styles.downloadInner}>
				<div className={styles.downloadGrid}>
					<div className={styles.downloadImgWrap}>
						<img
							src={sizedPhoto(guide.heroPhoto, 550)}
							srcSet={srcSetFor(guide.heroPhoto, 550) || undefined}
							alt={guide.name}
							loading="lazy"
							className={styles.coverImg}
						/>
					</div>
					<div className={styles.downloadContent}>
						{/* Reuses `blog.article.ctaKicker` — the same "Ready when you are" kicker the
						    country page's own `WaveDownloadCTA` already passes explicitly. */}
						<span className={styles.downloadKicker}>{ui("blog.article.ctaKicker", "Ready when you are")}</span>
						{/* Was raw JSX — "Plan {guide.name} <br /> in Stippl" — so the one line a
						    reader's eye lands on first stayed English while the kicker, body and
						    button around it translated. `blog.article.ctaTitle` is the same key the
						    sidebar CTA and the country page already fill, and all seven locales
						    carry it. The `<br />` goes with it: a forced break cannot survive a
						    translation whose words fall in a different order, and the column is
						    already narrow enough to wrap this on its own. */}
						<h2 className={styles.downloadTitle}>{fillUi(ui("blog.article.ctaTitle", "Plan {country} in Stippl"), { country: guide.name })}</h2>
						<p className={styles.downloadBody}>
							{ui("guide.chrome.downloadCtaBody", "Day-by-day planning, stays and budget in one app, with every place from this guide already on the map.")}
						</p>
						<a href={appstoreUrl} onClick={handleTrack} target="_blank" rel="noreferrer" className={styles.downloadCta}>
							{ui("guide.chrome.downloadApp", "Download the app")}
						</a>
					</div>
				</div>
			</div>
		</section>
	);
}

// ── Sidebar ──────────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════
// Country-guide-specific sections (DiscoverCountryPage), share the chunky icon
// set + generic chrome above; keep their own CountryData-shaped props and layout
// CSS (DiscoverCountryPage.module.scss) since that page's grid differs from the
// single-destination guide's.
// ═══════════════════════════════════════════════════════════════════════════
// flagcdn serves lowercase paths only: `w160/TH.png` is a 404, `w160/th.png` is a 200. Every
// caller gets its alpha-2 from a source that upper-cases it, so the normalisation belongs here
// rather than at each call site, one of which was rendering a broken flag in the breadcrumb.
/**
 * An author portrait, or the Stippl mark when there isn't one.
 *
 * `<img src="">` does not render nothing, it renders a broken-image icon with the alt text
 * spilling out of a 40px circle, which is what the byline looked like. The house byline
 * ("The Stippl editors") has no portrait by design, so an avatar-less author is the normal
 * case here rather than an edge one, and it should look deliberate.
 *
 * `onError` covers the other half: a real avatar URL that 404s falls back to the same mark
 * instead of the same broken icon.
 */
export function AuthorAvatar({ author }: { author: { name: string; avatar?: string } }) {
	const [failed, setFailed] = useState(false);
	if (!author.avatar || failed) {
		return (
			<span className={styles.authorAvatarFallback} role="img" aria-label={author.name}>
				<svg viewBox={`0 0 ${S_VB.w} ${S_VB.h}`} width="55%" height="55%" aria-hidden="true">
					<path d={S_PATH} fill="currentColor" />
				</svg>
			</span>
		);
	}
	return <img src={author.avatar} alt={author.name} className={styles.coverImg} onError={() => setFailed(true)} />;
}

export const flagUrl = (code: string) => `https://flagcdn.com/w160/${(code || "").toLowerCase()}.png`;

// ── The Stippl "S" — brand mark used in the hero photo window, download pill,
// sidebar card and sign-off. Exact path, ported verbatim from the reference. ────
const S_PATH =
	"M73.1074 416.95C62.7264 416.95 54.4857 425.243 54.687 435.475C54.8958 445.7 63.4721 454 73.8457 454C84.2268 454 92.4675 445.7 92.2662 435.475C92.0574 425.243 83.481 416.95 73.1074 416.95ZM274.643 140.241C320.001 128.182 338.071 101.327 338.108 70.3779C338.16 28.3988 304.146 0 248.385 0C160.556 0 0 94.5184 0 181.258C0 249.295 88.4404 271.369 148.393 285.42C208.352 299.462 254.694 312.222 254.694 328.808C254.694 345.387 201.812 382.757 116.638 409.784C107.51 412.819 102.177 420.977 104.713 428.003C107.256 435.028 116.504 437.519 125.841 435.222C161.317 426.474 328.734 388.201 328.734 312.841C328.734 237.482 148.333 206.659 148.333 192.952C148.333 178.879 237.937 149.996 274.643 140.241Z";
const S_VB = { w: 338, h: 454 };

export const StipplS = ({ size = 14, color = "white" }: { size?: number; color?: string }) => (
	<svg width={(size * S_VB.w) / S_VB.h} height={size} viewBox={`0 0 ${S_VB.w} ${S_VB.h}`} fill="none" aria-hidden>
		<path d={S_PATH} fill={color} />
	</svg>
);

// The "S" laid out horizontally, used as a CSS mask for the closing wave banner.
const S_WAVE_PATH =
	"M166.841 1269.22C143.15 1269.22 124.344 1294.46 124.803 1325.61C125.28 1356.73 144.852 1382 168.526 1382C192.217 1382 211.024 1356.73 210.564 1325.61C210.088 1294.46 190.515 1269.22 166.841 1269.22ZM626.774 426.901C730.286 390.192 771.524 308.445 771.607 214.234C771.727 86.4472 694.102 0 566.847 0C366.411 0 0 287.719 0 551.761C0 758.869 201.833 826.062 338.653 868.831C475.489 911.578 581.245 950.418 581.245 1000.91C581.245 1051.38 460.561 1165.13 266.184 1247.4C245.353 1256.64 233.182 1281.47 238.969 1302.86C244.773 1324.25 265.878 1331.83 287.187 1324.84C368.147 1298.21 750.215 1181.7 750.215 952.305C750.215 722.909 338.516 629.08 338.516 587.353C338.516 544.516 543.004 456.595 626.774 426.901Z";
const S_WAVE_VB = { w: 771.607, h: 1382 };
export const S_WAVE_ASPECT = S_WAVE_VB.h / S_WAVE_VB.w;
export const S_WAVE_MASK_URL = `url("data:image/svg+xml,${encodeURIComponent(
	`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${S_WAVE_VB.h} ${S_WAVE_VB.w}' preserveAspectRatio='none'><path transform='translate(0,${S_WAVE_VB.w}) rotate(-90)' d='${S_WAVE_PATH}' fill='black'/></svg>`
)}")`;

// S-shaped window onto a photo — the hero's background decoration, clipped to the exact "S" path.
let sIdCounter = 0;
export function SPhotoWindow({
	photo,
	height,
	rotate = 0,
	fadeColor,
	fadeSide = "right",
}: {
	photo: string;
	height: number;
	rotate?: number;
	fadeColor?: string;
	fadeSide?: "left" | "right";
}) {
	const width = (height * S_VB.w) / S_VB.h;
	const clipId = `stipplS-clip-${(sIdCounter += 1)}`;
	const fadeId = `stipplS-fade-${sIdCounter}`;

	// Tilt the clip SHAPE, never the <svg>. A CSS `transform: rotate()` on the element turns
	// the photo with it, so the horizon came out at -18deg and every building leaned — which is
	// what 86cbbc5px reported. Rotating the path instead leaves the image upright inside a
	// tilted S, the same "uprightPhoto" trick FeatureToolboxSection and the homepage hero use.
	const cx = S_VB.w / 2;
	const cy = S_VB.h / 2;

	// The rotated path sweeps outside the viewBox, and an image that only spans the viewBox
	// leaves the S's extremities empty. Cover the rotated bounding box instead.
	const rad = (Math.abs(rotate) * Math.PI) / 180;
	const iw = S_VB.w * Math.cos(rad) + S_VB.h * Math.sin(rad);
	const ih = S_VB.w * Math.sin(rad) + S_VB.h * Math.cos(rad);
	const ix = (S_VB.w - iw) / 2;
	const iy = (S_VB.h - ih) / 2;

	return (
		<svg width={width} height={height} viewBox={`0 0 ${S_VB.w} ${S_VB.h}`} style={{ display: "block", overflow: "visible" }} aria-label="">
			<defs>
				<clipPath id={clipId}>
					<path d={S_PATH} transform={rotate ? `rotate(${rotate} ${cx} ${cy})` : undefined} />
				</clipPath>
				{fadeColor && (
					<linearGradient id={fadeId} x1={fadeSide === "left" ? "1" : "0"} y1="0" x2={fadeSide === "left" ? "0" : "1"} y2="0">
						<stop offset="0.72" stopColor={fadeColor} stopOpacity="0" />
						<stop offset="0.9" stopColor={fadeColor} stopOpacity="0.6" />
						<stop offset="1" stopColor={fadeColor} stopOpacity="1" />
					</linearGradient>
				)}
			</defs>
			<g clipPath={`url(#${clipId})`}>
				<image href={photo} x={ix} y={iy} width={iw} height={ih} preserveAspectRatio="xMidYMid slice" />
				{fadeColor && <rect x={ix} y={iy} width={iw} height={ih} fill={`url(#${fadeId})`} />}
			</g>
		</svg>
	);
}

// ── Icon resolution for data-driven sections ─────────────────────────────────
const COUNTRY_FOR_WHO_ICONS: Record<string, { icon: GuideIconName; accent: string; bg: string }> = {
	backpack: { icon: "packing", accent: "#2e9e72", bg: "#e8f9f4" },
	food: { icon: "eatdrink", accent: "#e84d7e", bg: "#fdeef3" },
	culture: { icon: "culture", accent: "#7c3aed", bg: "#f1ecfd" },
	naturalFeature: { icon: "naturalFeature", accent: "#2f9bd6", bg: "#e9f4fb" },
	skiResort: { icon: "skiResort", accent: "#2f9bd6", bg: "#e9f4fb" },
	practicalities: { icon: "practicalities", accent: "#d98a12", bg: "#fef4e6" },
	couple: { icon: "heart", accent: "#e84d7e", bg: "#fdeef3" },
	heritage: { icon: "heritage", accent: "#2f9bd6", bg: "#e9f4fb" },
};

const CULTURE_ICONS: Record<string, GuideIconName> = {
	temple: "heritage",
	hands: "person",
	crown: "heritage",
	calm: "heart",
	craft: "culture",
	muaythai: "route",
};

const PRACTICAL_ICONS: Record<string, GuideIconName> = {
	Visa: "visum",
	"Entry & visa": "visum",
	Currency: "budget",
	Money: "budget",
	"Getting around": "route",
	"Internet & SIM": "esim",
	Connectivity: "esim",
	Health: "heart",
	Water: "naturalFeature",
	Safety: "practicalities",
	"Plugs & power": "documents",
	"Power & practicalities": "practicalities",
	Tipping: "budget",
	Language: "phrases",
};

const COUNTRY_MONTH_COLORS: Record<MonthRating, { bar: string; label: string }> = {
	best: { bar: "#13c892", label: "Best time" },
	good: { bar: "#7fd6ae", label: "Good" },
	ok: { bar: "#e8c96a", label: "Busy / hot" },
	poor: { bar: "#7db8e0", label: "Rainy season" },
};
const COUNTRY_MONTH_HEIGHT: Record<MonthRating, number> = { best: 100, good: 78, ok: 58, poor: 40 };

// ── Table of contents ──────────────────────────────────────────────────────
// Label/icon/colour for every anchor a guide can carry, country or destination. The list a
// given page actually shows is built by `countryTocIds` / `destinationTocIds` below, which
// drop any section whose component would render nothing — so the sidebar never links to an
// anchor that isn't on the page.
export const TOC_META: Record<string, { label: string; icon: GuideIconName; color: string; bg: string }> = {
	destinations: { label: "Top destinations", icon: "destinations", color: "#e84d7e", bg: "#fdeef3" },
	"for-who": { label: "Who it's for", icon: "person", color: "#13c892", bg: "#e8f9f4" },
	when: { label: "When to visit", icon: "sun", color: "#d98a12", bg: "#fef4e6" },
	festivals: { label: "Festivals", icon: "specialdays", color: "#7c3aed", bg: "#f1ecfd" },
	highlights: { label: "Not to miss", icon: "heart", color: "#7c3aed", bg: "#f1ecfd" },
	neighbourhoods: { label: "Neighbourhoods", icon: "neighbourhood", color: "#e84d7e", bg: "#fdeef3" },
	stay: { label: "Where to stay", icon: "bed", color: "#2e9e72", bg: "#e8f9f4" },
	food: { label: "What to eat", icon: "eatdrink", color: "#e84d7e", bg: "#fdeef3" },
	"fun-facts": { label: "Fun facts", icon: "culture", color: "#7c3aed", bg: "#f1ecfd" },
	culture: { label: "Culture & etiquette", icon: "culture", color: "#2f9bd6", bg: "#e9f4fb" },
	budget: { label: "Daily budget", icon: "budget", color: "#2e9e72", bg: "#e8f9f4" },
	practical: { label: "Practicalities", icon: "practicalities", color: "#33525c", bg: "#e8f0f2" },
	phrases: { label: "Useful phrases", icon: "phrases", color: "#7c3aed", bg: "#f1ecfd" },
	"local-tips": { label: "Local tips", icon: "practicalities", color: "#33525c", bg: "#e8f0f2" },
	faq: { label: "FAQ", icon: "faq", color: "#2f9bd6", bg: "#e9f4fb" },
};

// Each predicate mirrors the `return null` guard in the matching section component above —
// keep the two in step when a guard changes. Order matches the page's render order.
const COUNTRY_TOC: { id: string; present: (c: CountryData) => boolean }[] = [
	{ id: "destinations", present: (c) => c.destinations.some((g) => g.items.length > 0) },
	{ id: "for-who", present: (c) => c.forWho.length > 0 || !!c.lessIdealFor },
	{ id: "when", present: (c) => !!c.bestTime || !!c.whenToVisit || c.months.length > 0 || c.seasons.length > 0 },
	{ id: "festivals", present: (c) => c.specialDays.length > 0 },
	{ id: "highlights", present: (c) => c.notToMiss.length > 0 },
	{ id: "food", present: (c) => c.food.length > 0 },
	{ id: "culture", present: (c) => c.culture.length > 0 || c.etiquette.do.length > 0 || c.etiquette.dont.length > 0 },
	{ id: "budget", present: (c) => c.budget.tiers.length > 0 || c.budget.breakdown.length > 0 },
	{ id: "practical", present: (c) => c.practicalities.length > 0 },
	{ id: "phrases", present: (c) => c.phrases.length > 0 },
	{ id: "faq", present: (c) => c.faqs.length > 0 },
];

const DESTINATION_TOC: { id: string; present: (g: CityData) => boolean }[] = [
	{ id: "for-who", present: (g) => g.forWho.length > 0 || !!g.lessIdealFor },
	{ id: "when", present: (g) => !!g.bestTime || !!g.whenToGo || g.months.length > 0 || g.seasons.length > 0 },
	{ id: "highlights", present: (g) => g.highlights.length > 0 },
	{ id: "neighbourhoods", present: (g) => g.neighbourhoods.length > 0 },
	{ id: "stay", present: (g) => !!g.stays.intro || g.stays.areas.length > 0 || g.stays.hotels.length > 0 },
	{ id: "food", present: (g) => g.food.length > 0 },
	{ id: "culture", present: (g) => !!g.culture?.length || !!g.etiquette?.do?.length || !!g.etiquette?.dont?.length },
	{ id: "budget", present: (g) => !!g.budget?.tiers?.length || !!g.budget?.breakdown?.length },
	{ id: "fun-facts", present: (g) => g.funFacts.length > 0 },
	{ id: "local-tips", present: (g) => g.localTips.length > 0 },
	{ id: "faq", present: (g) => g.faqs.length > 0 },
];

export const countryTocIds = (c: CountryData): string[] => COUNTRY_TOC.filter((s) => s.present(c)).map((s) => s.id);
export const destinationTocIds = (g: CityData): string[] => DESTINATION_TOC.filter((s) => s.present(g)).map((s) => s.id);

// ── For who ──────────────────────────────────────────────────────────────────
export type ForWhoProps = Pick<CountryData, "name" | "forWho" | "lessIdealFor">;

export function ForWho({ name, forWho, lessIdealFor }: ForWhoProps) {
	const ui = useGuideUi();
	if (!forWho.length && !lessIdealFor) return null;
	return (
		<>
			<SectionHeading id="for-who" kicker="Is this your trip?" icon="person" tilt={-4} vars={{ name }}>
				Who {name} is for
			</SectionHeading>
			{forWho.length > 0 && (
				<div className={styles.forWhoGrid}>
					{forWho.map((f, i) => {
						const ic = Object.values(COUNTRY_FOR_WHO_ICONS)[i];
						const tilt = [-2.5, 2, -2, 2.5, -1.5, 2][i % 6];
						return (
							<div key={`${f.title}-${i}`} className={`${styles.pad5} ${styles.forWhoCard}`}>
								{!!ic && (
									<span className={styles.forWhoIcon} style={{ background: ic?.bg, transform: `rotate(${tilt}deg)` }}>
										<GuideIcon name={ic.icon} fill={ic.accent} size={20} />
									</span>
								)}
								{!!f.title && <p className={styles.forWhoTitle}>{f.title}</p>}
								<p className={styles.forWhoBody}>
									<RichText text={f.body} />
								</p>
							</div>
						);
					})}
				</div>
			)}
			{lessIdealFor && (
				<div className={styles.forWhoNote}>
					<p className={styles.forWhoNoteBody}>
						<span className={styles.forWhoNoteStrong}>{ui("guide.chrome.maybeNotForYou", "Maybe not for you: ")}</span>
						<RichText text={lessIdealFor} />
					</p>
				</div>
			)}
		</>
	);
}

// ── When to visit ────────────────────────────────────────────────────────────
export interface WhenToVisitProps extends Pick<CountryData, "name" | "bestTime" | "whenToVisit" | "months" | "seasons" | "planningNotes"> {
	onPlan: () => void;
}

export function WhenToVisit({ name, bestTime, whenToVisit, months, seasons, planningNotes, onPlan }: WhenToVisitProps) {
	// Above the early return, not below it: this component bails out when it has nothing to show,
	// and a hook after that branch would run on some renders and not others.
	const ui = useGuideUi();
	if (!bestTime && !whenToVisit && !months.length && !seasons.length) return null;
	return (
		<>
			{/* `dictId="whenCountry"`: this section shares its DOM anchor (`id="when"`) with the
			    destination guide's `WhenToGo` above, but the two H2s read differently ("visit" vs
			    "go to") and so cannot share one dictionary entry — see the `useHeading` doc comment. */}
			<SectionHeading id="when" kicker="Timing" icon="sun" tilt={3} vars={{ name }} dictId="whenCountry">
				When to visit {name}
			</SectionHeading>
			{bestTime && (
				<div className={styles.whenBestTimePill}>
					<GuideIcon name="sun" fill="currentColor" size={16} />
					<span className={styles.whenBestTimeText}>
						{ui("guide.chrome.bestTime", "Best time:")} <RichText text={bestTime} />
					</span>
				</div>
			)}
			{whenToVisit && <Para>{whenToVisit}</Para>}

			{months.length > 0 && (
				<div className={styles.weatherCard}>
					<div className={styles.weatherHeader}>
						{/* The same keys `WeatherByMonth` uses. This chart is a second, inline copy of that
						    component — the country page draws its own because the bars are sized by rating
						    (`COUNTRY_MONTH_HEIGHT`) where the destination page's are uniform — and the copy
						    was made before the dictionary existed, so it kept English literals while the
						    shared one was localised. A Dutch country page read "Weather by month · Best time
						    · Good" under Dutch prose. The values already exist in all seven locales. */}
						<span className={styles.weatherHeaderLabel}>{ui("blog.section.weatherByMonth", "Weather by month")}</span>
						<div className={styles.weatherLegend}>
							{(Object.keys(COUNTRY_MONTH_COLORS) as MonthRating[]).map((k) => (
								<span key={k} className={styles.weatherLegendItem}>
									<span className={styles.weatherLegendDot} style={{ background: COUNTRY_MONTH_COLORS[k].bar }} />
									<span className={styles.weatherLegendLabel}>{ui(`blog.section.weather.${k}`, COUNTRY_MONTH_COLORS[k].label)}</span>
								</span>
							))}
						</div>
					</div>
					<div className={styles.weatherChart}>
						{months.map((m) => (
							<div key={m.m} className={styles.weatherMonth}>
								<span className={styles.weatherTemp}>{m.temp}</span>
								<div
									className={styles.countryWeatherBar}
									style={{ height: COUNTRY_MONTH_HEIGHT[m.rating] ?? 58, background: (COUNTRY_MONTH_COLORS[m.rating] || UNKNOWN_MONTH).bar }}
									title={monthTooltip(
										monthAbbr(ui, m.m),
										COUNTRY_MONTH_COLORS[m.rating] ? ui(`blog.section.weather.${m.rating}`, COUNTRY_MONTH_COLORS[m.rating].label) : UNKNOWN_MONTH.label
									)}
								/>
								<span className={styles.weatherMonthLabel}>{monthAbbr(ui, m.m)}</span>
							</div>
						))}
					</div>
				</div>
			)}

			{seasons.length > 0 && (
				<div className={styles.seasonsGrid}>
					{seasons.map((s, i) => {
						// The same three tones the destination page cycles through. They were an inline
						// `style={{ color: tone }}` here; moving the destination variant onto
						// `SEASON_TONE_CLASSES` commented this one out and never replaced it, so every
						// country page lost the colour on its season periods while the destination
						// pages kept it. Found by comparing /discover/japan with /discover/japan/tokyo.
						// const seasonIcons: GuideIconName[] = ["sun", "island", "nationalPark"];
						return (
							<div key={`${s.name}-${s.period}-${i}`} className={`${styles.pad5} ${styles.countrySeasonCard}`}>
								{/* <span className={styles.countrySeasonIcon}>
									<GuideIcon name={seasonIcons[i % 3]} fill={SEASON_TONES[i % SEASON_TONES.length]} size={22} />
								</span> */}
								{!!s.name && <p className={styles.seasonName}>{s.name}</p>}
								<p className={`${styles.seasonPeriod} ${SEASON_TONE_CLASSES[i % SEASON_TONE_CLASSES.length]}`}>{s.period}</p>
								<p className={styles.seasonBody}>
									<RichText text={s.body} />
								</p>
							</div>
						);
					})}
				</div>
			)}
			<PlanningNotes notes={planningNotes} />
			<SaveWidget
				icon="calendar"
				title={fillUi(ui("guide.widget.whenCountry.title", "Add your {name} dates and we'll flag the weather per stop"), { name })}
				body={ui("guide.widget.whenCountry.body", "Stippl shows the season, crowds and prices for every place on your route.")}
				cta={ui("guide.widget.whenCountry.cta", "Start planning")}
				onClick={onPlan}
			/>
		</>
	);
}

// ── Festivals ────────────────────────────────────────────────────────────────
export type FestivalsProps = Pick<CountryData, "specialDays">;

export function Festivals({ specialDays }: FestivalsProps) {
	if (!specialDays.length) return null;
	return (
		<>
			<SectionHeading id="festivals" kicker="Worth planning around" icon="specialdays" tilt={-3}>
				Festivals & special days
			</SectionHeading>
			<div className={`${styles.cardMax720} ${styles.festivalsCard}`}>
				{specialDays.map((d) => (
					<div key={`${d.name}-${d.body}`} className={styles.festivalRow}>
						<span className={styles.festivalDate}>{d.period}</span>
						<div className={styles.minW0}>
							{!!d.name && <p className={styles.festivalName}>{d.name}</p>}
							<p className={styles.festivalBody}>
								<RichText text={d.body} />
							</p>
						</div>
					</div>
				))}
			</div>
		</>
	);
}

// ── Top destinations ─────────────────────────────────────────────────────────
const DEST_TILTS = [-2, 1.5, -1.5, 2, -1, 1.5, -2, 1];

// `.destCardImgWrap` is a 170x220 cover slot (full width x 220 on phones); coverSlotWidth sizes for
// the height, which also covers a phone-width card.
const DEST_CARD_PHOTO_WIDTH = coverSlotWidth(170, 220);

function DestinationCardCompact({
	d,
	tilt,
	country,
	countrySlug,
	hidden,
}: {
	d: CountryData["destinations"][number]["items"][number];
	tilt: number;
	/** Country display name, passed to PhotoFill so it can fetch a fallback photo when `d.photo` is missing. */
	country: string;
	countrySlug: string;
	/** Beyond the collapsed cut-off: hidden from view, still in the DOM and still crawlable. */
	hidden?: boolean;
}) {
	const history = useHistory();
	const [hover, setHover] = useState(false);
	// guideRoutes.tsx doesn't thread `lang` down to this component — the SSR loader only passes
	// `match`/`initialData` — so this derives it the same way the homepage sections do.
	const lang = useMarketingLang();
	// Prefer the slug the knowledge base assigned over one derived from the display name: the
	// two diverge whenever the name is abbreviated, and the link then 404s. "Khao Sok NP"
	// slugifies to `khao-sok-np` while the destination is `khao-sok-national-park` — one of the
	// most complete guides we have, reached from the country page by a dead link.
	const href = discoverPath(`/discover/${countrySlug}/${d.slug || slugify(d.name)}`, lang);
	// Every item that reaches this card has a page: the knowledge base drops the ones it has no
	// guide for and the resolver drops any whose explore document is unpublished. Nothing to
	// branch on here, which is the point — a card that renders but does not click is its own
	// dead end (Robin, 2026-09-03).
	const inner = (
		<div className={styles.destCardImgWrap} style={{ boxShadow: hover ? "0 18px 40px rgba(0,63,76,0.22)" : "0 6px 18px rgba(0,63,76,0.12)" }}>
			{/* Only the destinations the media layer covers have a photo; the rest carry "".
				    PhotoFill fetches a real fallback photo for the country/city instead of
				    rendering an <img> with an empty src (broken-image glyph) or nothing at all. */}
			{d.photo ? (
				// KB alt (`photoCredit.alt`) describes what's actually in the frame; the destination
				// name is only a fallback for guides synced before the KB rewrite (123yb6937nz).
				<img
					src={sizedPhoto(d.photo, DEST_CARD_PHOTO_WIDTH)}
					srcSet={srcSetFor(d.photo, DEST_CARD_PHOTO_WIDTH) || undefined}
					alt={d.photoCredit?.alt || d.name}
					className={styles.destCardImg}
					style={{ transform: hover ? "scale(1.06)" : "scale(1)" }}
				/>
			) : (
				<PhotoFill country={country} city={d.name} />
			)}
			<div className={styles.destCardGradient} />
			<span className={styles.destCardKind}>
				<KindBadge kind={d.kind} tilt={[-3, 2, -2, 3][Math.abs(d.name.length) % 4]} />
			</span>
			{/* <span className={styles.destCardGuideBadge}>Guide</span> */}
			<div className={styles.destCardFooter}>
				<p className={styles.destCardName}>{d.name}</p>
				{!!d.nights ? (
					<span className={styles.destCardNights}>
						<GuideIcon name="packing" fill="currentColor" size={13} />
						<span className={styles.destCardNightsText}>{d.nights}</span>
					</span>
				) : (
					<div className={styles.destCardNights} />
				)}
			</div>
		</div>
	);
	return (
		<a
			href={href}
			onClick={(e) => {
				e.preventDefault();
				history.push(href);
			}}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			className={`${styles.destCard}${hidden ? ` ${styles.destCardHidden}` : ""}`}
			style={{ transform: hover ? "rotate(0deg) translateY(-4px)" : `rotate(${tilt}deg)` }}
		>
			{inner}
		</a>
	);
}

export interface DestinationsProps extends Pick<CountryData, "name" | "slug" | "destinations"> {
	onPlan: () => void;
	/** the hub renders the route map (countryMap/): the "Build my route" banner opens it instead of the plan gate */
	routeMap?: boolean;
}

export function Destinations({ name, slug, destinations, onPlan, routeMap }: DestinationsProps) {
	const ui = useGuideUi();
	// Tab identity and the `groupIconMap` lookup below both key off `groupKey` — the lowercased
	// ENGLISH group string — rather than `group` itself, which is now translatable copy (see
	// exploreTranslation.ts). Falls back to `group.toLowerCase()` for a guide document written
	// before the knowledge base started sending `groupKey`: that fallback is only ever exercised
	// on English `group` text (translation of `group` didn't exist before this field did either),
	// so it reproduces the exact lookup this component always did. Every place the group string is
	// used as an identity — tab keys, `activeTab` comparisons, `destinations.find` — must derive
	// from this same helper, or filtering and the icon/colour lookup drift apart again.
	const groupIdentity = (g: CountryData["destinations"][number]) => g.groupKey ?? g.group.toLowerCase();
	const allItems = destinations.flatMap((g) => g.items);
	const tabs = [
		{ key: "all", label: ui("guide.chrome.destinationsAllTab", "All") },
		...destinations.filter((g) => g.items.length > 0).map((g) => ({ key: groupIdentity(g), label: g.group })),
	];
	const [activeTab, setActiveTab] = useState("all");

	const groupIconMap: Record<string, { icon: GuideIconName; color: string; bg: string }> = {
		cities: { icon: "city", color: "#1a7a5e", bg: "#d1f5ea" },
		"islands & beaches": { icon: "island", color: "#1a6fa8", bg: "#cce8f8" },
		"regions & towns": { icon: "region", color: "#5b28b5", bg: "#e5d9fb" },
		"national parks & nature": { icon: "nationalPark", color: "#276635", bg: "#c8f0d0" },
	};
	const allCfg = { color: "#13c892", bg: "#e8f9f4" };
	const total = allItems.length;
	const visibleItems = activeTab === "all" ? allItems : (destinations.find((g) => groupIdentity(g) === activeTab)?.items ?? []);
	// The grid used to cap at 12 with no way past it, so 16 of Japan's 28 destinations had no
	// route in from the country page at all — and the cap applies per tab, so even Cities (14)
	// truncated. Collapsed by default to keep the section scannable; expanding reveals the rest
	// in the grid that is already here rather than needing a page of its own.
	//
	// Every item is rendered; "collapsed" hides the overflow in CSS rather than slicing it out
	// of the array. These cards are the only links from a country guide to its destination
	// guides, and a link that exists only after a click on a client-side toggle is a link no
	// crawler follows — which would leave 16 of Japan's 28 destination pages reachable from
	// the sitemap alone. Same visual behaviour, a complete DOM underneath it.
	const [showAll, setShowAll] = useState(false);
	const COLLAPSED_COUNT = 12;
	const hiddenCount = showAll ? 0 : Math.max(0, visibleItems.length - COLLAPSED_COUNT);
	let globalIdx = 0;

	// Not a `SectionHeading`, so it reads the override itself. The default kicker counts the
	// cards; a copy file that hard-codes the number in its kicker owns keeping it true, which is
	// why the knowledge base resolves `{{count:destinations}}` rather than letting anyone type it.
	// `destinations` and `routes` render only on the country page, so their dictionary values are
	// written in the country form directly — no `*Country` variant needed, unlike the headings
	// shared with the destination layout.
	const destHeading = useHeading("destinations", `${total} destinations we'd go back to`, `Top ${name} destinations we recommend`, {
		vars: { name, total: String(total) },
	});

	if (!allItems.length) return null;

	return (
		<>
			<div id="destinations" className={styles.destinationsHeading}>
				<span className={styles.destinationsKicker}>{destHeading.kicker}</span>
				<h2 className={`${styles.notItalic} ${styles.destinationsTitle}`}>{destHeading.h2}</h2>
			</div>

			<div className={styles.destinationsTabs}>
				{tabs.map(({ key, label }) => {
					const count = key === "all" ? total : (destinations.find((g) => groupIdentity(g) === key)?.items.length ?? 0);
					const active = activeTab === key;
					const cfg = key === "all" ? allCfg : (groupIconMap[key] ?? { icon: "city" as GuideIconName, color: "#1a7a5e", bg: "#d1f5ea" });
					return (
						<button
							key={key}
							onClick={() => setActiveTab(key)}
							className={styles.destinationsTabBtn}
							style={{ borderColor: active ? cfg.color : "#cce0e5", background: active ? cfg.color : "white", color: active ? "white" : "#4a6e7a" }}
						>
							{key !== "all" && <GuideIcon name={(groupIconMap[key] ?? groupIconMap["cities"]).icon} fill={active ? "white" : cfg.color} size={16} />}
							{label}
							<span
								className={styles.destinationsTabCount}
								style={{ background: active ? "rgba(255,255,255,0.25)" : "#eef6f8", color: active ? "white" : "#8aabb5" }}
							>
								{count}
							</span>
						</button>
					);
				})}
			</div>

			<div className={styles.destinationsGrid}>
				{visibleItems.map((d, i) => {
					const tilt = DEST_TILTS[globalIdx++ % DEST_TILTS.length];
					return <DestinationCardCompact key={d.name} d={d} tilt={tilt} country={name} countrySlug={slug} hidden={!showAll && i >= COLLAPSED_COUNT} />;
				})}
			</div>

			{(hiddenCount > 0 || showAll) && (
				<div className={styles.destinationsViewAllWrap}>
					<button type="button" onClick={() => setShowAll((v) => !v)} className={styles.destinationsViewAll}>
						<span className={styles.destinationsViewAllIcon}>
							<GuideIcon name="marker" fill="white" size={20} />
						</span>
						<span className={styles.destinationsViewAllLabel}>
							{showAll
								? fillUi(ui("guide.chrome.showFewerDestinations", "Show fewer destinations in {name}"), { name })
								: fillUi(ui("guide.chrome.viewAllDestinations", "View all {count} destinations in {name}"), { count: String(total), name })}
						</span>
						<span className={styles.destinationsViewAllIcon}>
							<svg
								width="16"
								height="16"
								viewBox="0 0 24 24"
								fill="none"
								stroke="white"
								strokeWidth="2.5"
								strokeLinecap="round"
								strokeLinejoin="round"
								style={{ transform: showAll ? "rotate(-90deg)" : "none", transition: "transform 150ms" }}
							>
								<path d="M5 12h14M13 6l6 6-6 6" />
							</svg>
						</span>
					</button>
				</div>
			)}

			{/* Reuses `blog.block.routeCta.*` rather than a new `guide.widget.route.*` key: this is
			    the exact same card `blockRegistry.tsx` already renders inline in a blog article
			    (same English copy, already translated in all seven locales), just embedded here
			    at the bottom of the country page's destinations grid instead. */}
			<SaveWidget
				icon="route"
				title={fillUi(ui("blog.block.routeCta.titleWithWhere", "Turn these stops into a {where} route"), { where: name })}
				body={ui("blog.block.routeCta.body", "Pick your favourites and Stippl orders them, adds travel times and builds the day-by-day.")}
				cta={ui("blog.block.routeCta.cta", "Build my route")}
				onClick={
					routeMap
						? () => {
								// A phone has no builder tab (the section hides it), so it opens the routes; the check is at click time, as planGate's.
								openRouteMapTab(isMobileOnly ? "routes" : "mine");
								scrollToRouteMap();
							}
						: onPlan
				}
			/>
		</>
	);
}

// ── More in {country} ────────────────────────────────────────────────────────
/**
 * The destination guide's sideways links: the country's other destinations, same cards and grid as
 * the country page's "Top destinations". A destination guide used to link only up to its country
 * and out to the footer, which left every one of them a near-leaf for crawlers.
 *
 * Every card is rendered in the DOM on the server (nothing hides behind a toggle), and the hrefs
 * come out of `DestinationCardCompact`'s own builder. The list is the resolver's `guide.siblings`,
 * which already holds only destinations that pass the readiness gate.
 */
export function SiblingDestinations({
	country,
	countrySlug,
	siblings,
}: {
	/** The country's display name, for the heading and the card's photo fallback. */
	country: string;
	countrySlug: string;
	siblings: NonNullable<CityData["siblings"]>;
}) {
	const ui = useGuideUi();
	if (!siblings.length) return null;
	return (
		<section id="more-in-country">
			<div className={styles.destinationsHeading}>
				<h2 className={`${styles.notItalic} ${styles.destinationsTitle}`}>{fillUi(ui("guide.chrome.moreInCountry", "{country}: more destinations"), { country })}</h2>
			</div>
			<div className={`${styles.destinationsGrid} ${styles.siblingsGrid}`}>
				{siblings.map((s, i) => (
					<DestinationCardCompact
						key={s.slug}
						d={{ slug: s.slug, name: s.name, kind: s.kind || "City", photo: s.photo || "", blurb: "", nights: "" }}
						tilt={DEST_TILTS[i % DEST_TILTS.length]}
						country={country}
						countrySlug={countrySlug}
					/>
				))}
			</div>
		</section>
	);
}

// ── AI banner (mid-page) ─────────────────────────────────────────────────────
export interface AIPlanBannerProps extends Pick<CountryData, "name"> {
	onPlan: () => void;
}

export function AIPlanBanner({ name, onPlan }: AIPlanBannerProps) {
	const ui = useGuideUi();
	const prompts = [
		fillUi(ui("blog.section.aiPrompt1", "3 weeks in {name}, islands and food"), { name }),
		fillUi(ui("blog.section.aiPrompt2", "{name} with kids, no long bus rides"), { name }),
		fillUi(ui("blog.section.aiPrompt3", "First time in {name}, 14 days"), { name }),
	];
	return (
		<div className={styles.aiBanner}>
			<div className={styles.aiBannerGlow} />
			<div className={styles.aiBannerInner}>
				<span className={styles.aiBannerKicker}>
					<GuideIcon name="ai" fill="currentColor" size={15} />
					<span className={styles.aiBannerKickerText}>{ui("blog.section.aiKicker", "AI trip planner")}</span>
				</span>
				<h3 className={styles.aiBannerTitle}>{fillUi(ui("blog.section.aiTitle", "Describe your trip, get a {name} itinerary in seconds"), { name })}</h3>
				<p className={styles.aiBannerBody}>
					{ui(
						"blog.section.aiBody",
						"Everything in this guide is already in Stippl. Tell the planner how long you have and what you like: it builds the route, the days and the travel times, and you edit whatever you want."
					)}
				</p>
				<div className={styles.aiBannerPrompts}>
					{prompts.map((t) => (
						<span key={t} className={styles.aiBannerPrompt}>
							"{t}"
						</span>
					))}
				</div>
				<button onClick={onPlan} className={styles.aiBannerCta}>
					<GuideIcon name="ai" fill="white" size={16} />
					{fillUi(ui("blog.section.aiCta", "Plan {name} with AI"), { name })}
				</button>
			</div>
		</div>
	);
}

// ── What not to miss ─────────────────────────────────────────────────────────
export type NotToMissProps = Pick<CountryData, "name" | "notToMiss">;

export function NotToMiss({ name, notToMiss }: NotToMissProps) {
	if (!notToMiss.length) return null;
	const photos = notToMiss.filter((h) => h.photo).slice(0, 3);
	return (
		<>
			{/* `dictId="highlightsCountry"`: this and the destination guide's `Highlights` above
			    render the same English H2 ("What not to miss in {name}"), but fr/it/pt need a
			    different construction for a country name than for a city — see the `Food`/`Budget`
			    doc comments below for the full reasoning. */}
			<SectionHeading id="highlights" kicker="Highlights" icon="heart" tilt={4} vars={{ name }} dictId="highlightsCountry">
				What not to miss in {name}
			</SectionHeading>
			{photos.length > 0 && (
				<div className={styles.notToMissPhotos}>
					{photos.map((h) => (
						<figure key={h.photo} className={styles.notToMissPhoto}>
							{/* Same fallback chain as the destination guide's `Highlights` above: KB alt first,
							    title/body only for a guide synced before 123yb6937nz. */}
							<img
								src={sizedPhoto(h.photo!, 720)}
								srcSet={srcSetFor(h.photo!, 720) || undefined}
								alt={h.photoCredit?.alt || h.title || h.body}
								className={styles.coverImg}
							/>
							<PhotoCredit credit={h.photoCredit} />
						</figure>
					))}
				</div>
			)}
			<div className={styles.notToMissList}>
				{notToMiss.map((h, i) => (
					<div key={`${h.title}-${i}`} className={styles.notToMissRow}>
						<span className={styles.notToMissIndex}>{String(i + 1).padStart(2, "0")}</span>
						<div className={styles.minW0}>
							{!!h.title && <p className={styles.notToMissTitle}>{h.title}</p>}
							<p className={styles.notToMissBody}>
								<RichText text={h.body} />
							</p>
						</div>
					</div>
				))}
			</div>
		</>
	);
}

// ── Food ─────────────────────────────────────────────────────────────────────
export interface FoodProps extends Pick<CountryData, "name" | "food"> {
	onPlan: () => void;
	/**
	 * True from `DiscoverCountryPage` only — `Food` is one component shared by both guide pages.
	 * "What to eat in {name}" is fine either way in en/nl/de/es, but fr/it/pt govern {name} with
	 * a preposition that a city takes bare ("à Bangkok", "a Bangkok", "em Bangkok") and a country
	 * takes with an article that varies by gender and number ("au Japon", "en France", "aux
	 * Pays-Bas") — no single fixed preposition is right for every country, so those locales point
	 * this at a distinct `guide.section.foodCountry.h2` key instead (Italian is the one exception:
	 * "in {name}" is right for essentially every country, so it swaps the preposition rather than
	 * restructuring the sentence). Defaults to the destination form so nothing about the (much
	 * more common) destination render changes.
	 */
	isCountry?: boolean;
}

/**
 * Split the dishes into the two featured cards and the plain list beneath them.
 *
 * The featured slots go to dishes we actually have a curated photo for, in dossier order — not
 * to whichever two the dossier happens to list first (123yb691r1a).
 *
 * `food.slice(0, 2)` left 51 featured cards rendering as a text-only box beside a photo card,
 * which reads as broken rather than sparse. 41 of them had a perfectly good photo in
 * `explore/media/` sitting on a dish that had been pushed into the plain list below: Germany
 * featured Schnitzel and Käsespätzle with no images while Weisswurst and Döner kebab, both
 * photographed, sat in the text list.
 *
 * A presentation choice, not a data one, so it lives here rather than reordering `doc["food"]`
 * in the builder — the list keeps the dossier's editorial order, which is the order a writer
 * chose. Exported for its test; `Food` is the only caller.
 */
export function splitFeaturedFood<T extends { name: string; photo?: string }>(food: T[]): { featured: T[]; rest: T[] } {
	const featured = food.filter((f) => f.photo).slice(0, 2);
	const featuredNames = new Set(featured.map((f) => f.name));
	return { featured, rest: food.filter((f) => !featuredNames.has(f.name)) };
}

export function Food({ name, food, onPlan, isCountry = false }: FoodProps) {
	const ui = useGuideUi();
	if (!food.length) return null;
	const { featured, rest } = SHOW_FOOD_PHOTOS ? splitFeaturedFood(food) : { featured: [], rest: food };
	return (
		<>
			<SectionHeading id="food" kicker="Eat like a local" icon="eatdrink" tilt={-3} vars={{ name }} dictId={isCountry ? "foodCountry" : undefined}>
				What to eat in {name}
			</SectionHeading>
			{featured.length > 0 && (
				<div className={styles.foodFeaturedGrid}>
					{featured.map((f) => (
						<div key={f.name} className={`${styles.overflowHidden} ${styles.foodFeaturedCard}`}>
							<div className={styles.foodFeaturedImgWrap}>
								<img src={sizedPhoto(f.photo, 350)} srcSet={srcSetFor(f.photo, 350) || undefined} alt={f.name} className={styles.coverImg} />
								<PhotoCredit credit={f.photoCredit} />
							</div>
							<div className={styles.foodFeaturedBody}>
								<div className={styles.foodFeaturedNameRow}>
									<p className={styles.foodFeaturedName}>{f.name}</p>
									<span className={styles.foodFeaturedLocal}>{f.local}</span>
								</div>
								<p className={styles.foodFeaturedDesc}>
									<RichText text={f.body} />
								</p>
							</div>
						</div>
					))}
				</div>
			)}
			{rest.length > 0 && (
				<div className={`${styles.cardMax720} ${styles.foodListCard}`}>
					{rest.map((f) => (
						<div key={f.name} className={styles.foodListRow}>
							<span className={styles.foodListDot} />
							<div className={styles.minW0}>
								<span className={styles.foodListName}>{f.name}</span>
								<span className={styles.foodListLocal}>{f.local}</span>
								<p className={styles.foodListDesc}>
									<RichText text={f.body} />
								</p>
							</div>
						</div>
					))}
				</div>
			)}
			{/* Reuses `blog.block.food.*` / `blog.block.discoverCta` rather than a new
			    `guide.widget.food.*` key — the identical card `blockRegistry.tsx` already renders
			    inline in a blog article, same English copy, already translated in all seven
			    locales. */}
			<SaveWidget
				icon="eatdrink"
				title={ui("blog.block.food.title", "Discover food spots in Stippl")}
				body={ui("blog.block.food.body", "Find restaurants and markets on the map, save them to your itinerary, works offline too.")}
				cta={ui("blog.block.discoverCta", "Explore in Stippl")}
				tone="purple"
				onClick={onPlan}
			/>
		</>
	);
}

// ── Culture ──────────────────────────────────────────────────────────────────
export type CultureProps = Pick<CountryData, "culture" | "etiquette">;

/**
 * The DO / DON'T etiquette pair, on its own.
 *
 * `Culture` renders it under a section heading and after the cultural-highlights list, which
 * is what a country guide wants. An article drops a `do-dont` block mid-prose and wants
 * neither. Extracted rather than reimplemented so the two cannot drift into two designs.
 */
export function DoDont({ etiquette }: { etiquette: CultureProps["etiquette"] }) {
	const ui = useGuideUi();
	if (!etiquette?.do?.length && !etiquette?.dont?.length) return null;
	return (
		<div className={styles.etiquetteGrid}>
			{(
				[
					[ui("blog.section.do", "Do"), etiquette.do, "#13c892", "#e8f9f4"],
					[ui("blog.section.dont", "Don't"), etiquette.dont, "#e8734d", "#fdeee8"],
				] as const
			)
				.filter(([, items]) => items.length > 0)
				.map(([label, items, tone, bg]) => (
					<div key={label} className={`${styles.pad5} ${styles.etiquetteCard}`} style={{ background: bg }}>
						<p className={styles.etiquetteLabel} style={{ color: tone }}>
							{label}
						</p>
						<ul className={styles.etiquetteList}>
							{items.map((t) => (
								<li key={t} className={styles.etiquetteItem}>
									<span className={styles.etiquetteDot} style={{ background: tone }} />
									<span className={styles.etiquetteItemText}>
										<RichText text={t} />
									</span>
								</li>
							))}
						</ul>
					</div>
				))}
		</div>
	);
}

export function Culture({ culture, etiquette }: CultureProps) {
	if (!culture.length && !etiquette.do.length && !etiquette.dont.length) return null;
	return (
		<>
			<SectionHeading id="culture" kicker="Know before you go" icon="culture" tilt={3}>
				Cultural highlights & etiquette
			</SectionHeading>
			{culture.length > 0 && (
				<div className={styles.cultureList}>
					{culture.map((h, i) => (
						<div key={`${h.title}-${i}`} className={styles.cultureRow}>
							<span className={`${styles.iconCircle} ${styles.cultureIcon}`}>
								<GuideIcon name={Object.values(CULTURE_ICONS)[i] ?? "heritage"} fill="#0f3e4a" size={19} />
							</span>
							<div className={styles.minW0}>
								{!!h.title && <p className={styles.cultureTitle}>{h.title}</p>}
								<p className={styles.cultureBody}>
									<RichText text={h.body} />
								</p>
							</div>
						</div>
					))}
				</div>
			)}

			<DoDont etiquette={etiquette} />
		</>
	);
}

// ── Budget ───────────────────────────────────────────────────────────────────
export interface BudgetProps extends Pick<CountryData, "name" | "budget"> {
	onPlan: () => void;
	/** True from `DiscoverCountryPage` only — see the identical doc comment on `FoodProps`. */
	isCountry?: boolean;
}

/**
 * The three price tiers, on their own.
 *
 * `Budget` renders these inside a section heading and above a breakdown table, which is what a
 * country guide wants and not what an article does — an article drops a `daily-budget` block
 * mid-prose. Extracted rather than reimplemented so the two cannot drift into two designs.
 */
export function BudgetTiers({ tiers }: { tiers: BudgetProps["budget"]["tiers"] }) {
	if (!tiers?.length) return null;
	return (
		<div className={styles.budgetTiers}>
			{tiers.map((t) => {
				const dark = !!t.highlight;
				// Real data pairs "€X / $Y" — shrink the USD half so the price stays on one line
				// instead of wrapping unpredictably by card width.
				const [eur, usd] = t.perDay.split(" / ");
				return (
					<div
						key={t.name}
						className={styles.budgetTier}
						style={{ background: dark ? "#e8f9f4" : "white", borderWidth: 1.5, borderStyle: "solid", borderColor: dark ? "#13c892" : "#cce0e5" }}
					>
						<p className={styles.budgetTierName} style={{ color: dark ? "#13c892" : "#8aabb5" }}>
							{t.name}
						</p>
						<p className={styles.budgetTierPrice}>
							{eur}
							{usd && <span className={styles.budgetTierPriceAlt}> / {usd}</span>}
						</p>
						<p className={styles.budgetTierBody}>
							<RichText text={t.body} />
						</p>
					</div>
				);
			})}
		</div>
	);
}

export function Budget({ name, budget, onPlan, isCountry = false }: BudgetProps) {
	const ui = useGuideUi();
	if (!budget.tiers.length && !budget.breakdown.length) return null;
	return (
		<>
			<SectionHeading id="budget" kicker="What it costs" icon="budget" tilt={-4} vars={{ name }} dictId={isCountry ? "budgetCountry" : undefined}>
				Daily budget in {name}
			</SectionHeading>
			<BudgetTiers tiers={budget.tiers} />
			{budget.breakdown.length > 0 && (
				<div className={styles.cardMax720}>
					<div className={styles.budgetBreakdownHeader}>
						<GuideIcon name="budget" fill="currentColor" size={15} />
						<span className={styles.budgetBreakdownHeaderText}>{ui("guide.chrome.typicalPrices", "Typical prices")}</span>
					</div>
					<div className={styles.budgetBreakdownGrid}>
						{budget.breakdown.map((b) => (
							<div key={b.label} className={styles.budgetBreakdownRow}>
								<span className={styles.budgetBreakdownLabel}>{b.label}</span>
								<span className={styles.budgetBreakdownValue}>
									<RichText text={b.value} plain />
								</span>
							</div>
						))}
					</div>
				</div>
			)}
			{/* Reuses `blog.block.budgetTracker.*` rather than a new `guide.widget.budget.*` key —
			    the identical card `blockRegistry.tsx` already renders inline in a blog article,
			    same English copy, already translated in all seven locales. */}
			<SaveWidget
				icon="budget"
				title={ui("blog.block.budgetTracker.title", "Track what you actually spend")}
				body={ui("blog.block.budgetTracker.body", "Set a daily budget per country and log expenses in any currency while you travel.")}
				cta={ui("blog.block.budgetTracker.cta", "Try the budget tracker")}
				tone="amber"
				onClick={onPlan}
			/>
		</>
	);
}

// ── Practicalities ───────────────────────────────────────────────────────────
export type PracticalitiesProps = Pick<CountryData, "name" | "practicalities">;

export function Practicalities({ name, practicalities }: PracticalitiesProps) {
	if (!practicalities.length) return null;
	return (
		<>
			<SectionHeading id="practical" kicker="Practical" icon="practicalities" tilt={3}>
				Practicalities
			</SectionHeading>
			<div className={styles.practicalGrid}>
				{practicalities.map((p) => (
					<div key={p.label} className={`${styles.pad5} ${styles.practicalCard}`}>
						<div className={styles.practicalHeader}>
							<span className={`${styles.iconCircle} ${styles.practicalIcon}`}>
								<GuideIcon name={PRACTICAL_ICONS[p.label] ?? "route"} fill="#33525c" size={16} />
							</span>
							<p className={styles.practicalLabel}>{p.label}</p>
						</div>
						<p className={styles.practicalValue}>
							<RichText text={p.value} plain />
						</p>
						{p.note && (
							<p className={styles.practicalNote}>
								<RichText text={p.note} />
							</p>
						)}
					</div>
				))}
			</div>
			{/* The eSIM widget that sat here is hidden with the article blocks (123yb691qvb):
			    the eSIM is a mobile feature, and this CTA read "See <country> eSIMs" and pushed
			    /register, since SaveWidget falls back to signup when given no onClick. */}
		</>
	);
}

// ── Phrases ──────────────────────────────────────────────────────────────────
export type PhrasesProps = Pick<CountryData, "phrases">;

export function Phrases({ phrases }: PhrasesProps) {
	const ui = useGuideUi();
	if (!phrases.length) return null;
	return (
		<>
			<SectionHeading id="phrases" kicker="Say hello" icon="phrases" tilt={-3}>
				Useful phrases
			</SectionHeading>
			<Para>{ui("guide.chrome.phrasesIntro", "Nobody expects you to speak the language, which is exactly why a handful of words goes so far.")}</Para>
			<div className={styles.phrasesGrid}>
				{phrases.map((p) => (
					<div key={p.latin} className={styles.phraseCard}>
						<span className={`${styles.iconCircle} ${styles.phraseIcon}`}>
							<GuideIcon name="phrases" fill="currentColor" size={16} />
						</span>
						<div className={styles.minW0}>
							<p className={styles.phraseLatin}>{p.latin}</p>
							<p className={styles.phraseTranslation}>
								{p.en} · <span className={styles.phraseThai}>{p.native}</span>
							</p>
						</div>
					</div>
				))}
			</div>
		</>
	);
}

// ── Routes Section ────────────────────────────────────────────────────────────
const ROUTE_TILTS = [-3.5, 2.5, -2, 3, -2.5];

function RouteCard({
	route,
	idx,
	mapEmbed,
	countryCode,
}: {
	route: NonNullable<CountryData["routes"]>[number];
	idx: number;
	mapEmbed: string;
	countryCode: string;
}) {
	const [hov, setHov] = useState(false);
	const ui = useGuideUi();
	const mapRef = useRef<HTMLDivElement>(null);
	// 300px enter / 900px exit — see `useInViewport` above `LazyGuideMap`. `.routesScroller` is
	// a horizontal carousel, so all cards in it sit in the same vertical band; native
	// `loading="lazy"` on the iframe below does not defer any of them on its own (123yb69326d).
	const inView = useInViewport(mapRef, { rootMargin: "300px", exitRootMargin: "900px" });
	const tilt = ROUTE_TILTS[idx % ROUTE_TILTS.length];
	// `id` (the trip's own `_id`) rather than the username-based `url` — resolves through
	// useGraphTrip's `isRoutePreview` branch (PUBLIC_TRIP by id), same as JoinTrip's /join/:tripId,
	// and is never null the way the owner-username link can be. The language rides as `?lang=`
	// (the page is an unprefixed app route), from `GuideLangContext` for SSR-correct HTML like
	// `RichText`'s links, not `useMarketingLang()`.
	const lang = React.useContext(GuideLangContext);
	const routePreviewUrl = withRoutePreviewLang(`/routepreview/${route.id}`, lang);
	// `route.duration` is a human-written duration head off the trip's own name ("2 weeks — a
	// first-timer classic" -> "2 weeks"), when the trip that seeded this route had one.
	// `splitTitle` (graphql/schemas/explore.ts) returns `null` instead of falling back to an
	// English-derived string when it doesn't, so this formats the wire's numeric `nights` through
	// the dictionary rather than shipping "7 nights" on every locale (123yb6937p5). `nights` rides
	// along on the same route object regardless of which branch `duration` took.
	//
	// `Number.isFinite`, not a truthiness check or the type alone: `nights` is typed required but
	// arrives as untyped JSON (`exploreGuide` is an unmodelled blob), so a payload written before
	// this field existed — anything still in the SSR cache across the deploy — would render the
	// string "undefined nights" into the badge. An empty badge is the right degradation; a
	// zero-night route has no duration worth printing either, which `isFinite` alone would let
	// through as "0 nights", hence the `> 0`.
	const nightsKey = route.nights === 1 ? "guide.routes.night" : "guide.routes.nights";
	const nightsFallback = route.nights === 1 ? "{count} night" : "{count} nights";
	const derivedDuration = Number.isFinite(route.nights) && route.nights > 0 ? fillUi(ui(nightsKey, nightsFallback), { count: String(route.nights) }) : "";
	const durationLabel = route.duration ?? derivedDuration;
	return (
		<a
			href={routePreviewUrl}
			onMouseEnter={() => setHov(true)}
			onMouseLeave={() => setHov(false)}
			className={styles.routeCard}
			style={{
				borderColor: hov ? "#13c892" : "#cce0e5",
				boxShadow: hov ? "0 24px 52px rgba(19,200,146,0.14), 0 4px 14px rgba(0,0,0,0.07)" : "0 4px 20px rgba(15,62,74,0.10)",
				transform: `rotate(${hov ? 0 : tilt}deg)`,
				opacity: 1,
			}}
		>
			<div ref={mapRef} className={`${styles.relativeFull} ${styles.routeCardMap}`}>
				{/* An <iframe> with an empty src loads the current page into itself. Countries
				    without a synced trip carry no embed, so render nothing rather than a frame.

				    Also gated on `inView` — mounted only within 300px of the viewport, unmounted
				    again past 900px — so the 12 embeds in this carousel don't all boot their own
				    MapLibre context at once (123yb69326d). `.routeCardMap` carries its own
				    background, so unmounting the iframe causes no layout shift. */}
				{mapEmbed && inView && <iframe src={mapEmbed} title={route.title} loading="lazy" className={`${styles.absoluteFillImg} ${styles.noBorder}`} />}
				<div className={styles.routeCardDurationBadge}>
					<span className={`${styles.circleBadge} ${styles.routeCardFlag}`}>
						<img src={flagUrl(countryCode)} alt="" className={styles.coverImg} />
					</span>
					<span className={styles.routeCardDuration}>{durationLabel}</span>
				</div>
			</div>
			<div className={styles.routeCardBody}>
				<div className={styles.routeCardMeta}>
					<GuideIcon name="calendar" fill="var(--color-primary)" size={14} />
					<span className={styles.routeCardMetaText}>
						{ui("guide.routes.bestTime", "Best time:")} <strong className={styles.routeCardMetaStrong}>{route.bestTime}</strong>
					</span>
				</div>
				<p className={styles.routeCardTitle}>{route.title}</p>
				<p className={styles.routeCardSubtitle}>{route.subtitle}</p>
				<p className={styles.routeCardDesc}>
					<RichText text={route.desc} />
				</p>
				<div className={styles.routeCardFooter}>
					{/* Visual affordance only, not a real link — the whole card (the outer `<a>`) is
					    the click target now, and HTML doesn't allow a nested `<a>`. */}
					<span
						className={styles.routeCardLink}
						style={{
							background: hov ? "#13c892" : "#e8f9f4",
							color: hov ? "white" : "#13c892",
						}}
					>
						{ui("guide.routes.viewRoute", "View route")}
						<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
							<path d="M5 12h14M13 6l6 6-6 6" />
						</svg>
					</span>
				</div>
			</div>
		</a>
	);
}

export type RoutesSectionProps = Pick<CountryData, "name" | "countryCode" | "mapEmbed" | "routes">;

export function RoutesSection({ name, countryCode, mapEmbed, routes }: RoutesSectionProps) {
	const ui = useGuideUi();
	const routesHeading = useHeading("routes", `Plan your adventure in ${name}`, `Our travel routes through ${name}`, { vars: { name } });
	if (!routes?.length) return null;
	return (
		<section className={styles.routesSection}>
			<div className={styles.routesHeader}>
				<div className={styles.routesHeaderRow}>
					<div>
						<p className={styles.routesKicker}>{routesHeading.kicker}</p>
						<h2 className={styles.routesTitle}>{routesHeading.h2}</h2>
						<p className={styles.routesDesc}>
							{fillUi(
								ui(
									"guide.chrome.routesIntro",
									"{name} is loved by culture seekers and backpackers alike. We recommend several routes through this country, here they are in full!"
								),
								{ name }
							)}
						</p>
					</div>
					{/* "All routes" is hidden on the country page (123yb691qu7). It was a dead
					    `href="#"`, and there is no all-routes page for it to go to; the scroller
					    beside it already carries every route this country has. */}
				</div>
			</div>
			<div className={styles.routesScroller}>
				<div className={styles.routesTrack}>
					{routes.map((route, i) => (
						// Each route shows its own map. Passing the country-level `mapEmbed` to
						// every card rendered the same map on all of them, the prototype has the
						// same tell, five iframes pointing at one trip.
						<RouteCard key={route.id} route={route} idx={i} mapEmbed={route.mapEmbed || mapEmbed} countryCode={countryCode} />
					))}
				</div>
			</div>
		</section>
	);
}

// ── Sidebar, shared by both the country and destination guide pages ───────────────
export interface SidebarProps extends Pick<CountryData, "name" | "mapEmbed" | "countryCode"> {
	onPlan: () => void;
	// Destination guides only, links back to the parent country guide.
	/**
	 * `href` is required, not decorative: this is the only link from a destination guide up to
	 * its country guide, so with `href="#"` (as it was) a crawler reaching a destination page
	 * had no route to the country one. The onClick still handles in-app navigation.
	 */
	partOfCountry?: { name: string; countryCode: string; href: string; onClick: () => void };
	/** Mirrors the hero's map, same markers, same subject (Luuk's map spec). */
	mapMarkers?: GuideMapMarker[];
	isCountry?: boolean;
	/**
	 * Ordered anchor ids of the sections this guide actually renders — from `countryTocIds` /
	 * `destinationTocIds`. The menu shows exactly these, so it never links to a section the
	 * guide left empty (and never omits one it filled).
	 */
	sections: string[];
}

/**
 * `TOC_META[id].label` translated for this sidebar. EVERY id that has prose needs an entry —
 * `Sidebar` renders `TOC_META[id].label` directly, so an id missing from this map renders its
 * English literal no matter what else on the page says the same word in Dutch.
 *
 * Most of these reuse a `guide.nav.*` key rather than minting a second one: the Hero's section
 * grid above already names the same sections, and two keys for one word drift apart the first
 * time someone edits only one of them. Matched by the ENGLISH TEXT, not by id — the ids do not
 * line up (this table's `highlights` reads "Not to miss", which is `guide.nav.notToMiss`, while
 * `guide.nav.highlights` is the destination page's "Highlights").
 *
 * `faq` is deliberately absent: "FAQ" is the same in all seven.
 */
export const TOC_LABEL_KEYS: Record<string, string> = {
	destinations: "guide.chrome.topDestinations",
	"fun-facts": "guide.chrome.funFacts",
	culture: "guide.chrome.cultureEtiquette",
	phrases: "guide.chrome.usefulPhrases",
	"for-who": "guide.nav.forWho.label",
	when: "guide.nav.when.label",
	festivals: "guide.nav.festivals.label",
	highlights: "guide.nav.notToMiss.label",
	neighbourhoods: "guide.nav.neighbourhoods.label",
	stay: "guide.nav.stays.label",
	food: "guide.nav.eat.label",
	budget: "guide.nav.budget.label",
	practical: "guide.nav.practical.label",
	"local-tips": "guide.nav.localTips.label",
};

export function Sidebar({ name, countryCode, onPlan, partOfCountry, mapMarkers, isCountry, sections }: SidebarProps) {
	const { ref, top } = useDirectionalSticky();
	const ui = useGuideUi();
	// 14 of TOC_META's 15 entries resolve through TOC_LABEL_KEYS; only "FAQ" stays literal.
	const items = useMemo(
		() =>
			sections.flatMap((id) =>
				TOC_META[id] ? [{ id, ...TOC_META[id], label: TOC_LABEL_KEYS[id] ? ui(TOC_LABEL_KEYS[id], TOC_META[id].label) : TOC_META[id].label }] : []
			),
		[sections, ui]
	);
	const activeId = useScrollSpy(items.map((t) => t.id));
	const { appstoreUrl, handleTrack } = useAppStoreLink("discover");
	return (
		<aside className={styles.sidebar}>
			<div ref={ref} className={styles.sidebarSticky} style={{ top }}>
				<div className={`${styles.overflowHidden} ${styles.sidebarMapCard}`}>
					<div className={`${styles.relativeFull} ${styles.sidebarMap}`}>
						<div className={styles.absoluteFillImg}>
							<LazyGuideMap id="sidebar" markers={mapMarkers || []} countryName={isCountry ? name : undefined} />
						</div>
						<div className={styles.sidebarMapBadge}>
							<span className={`${styles.circleBadge} ${styles.sidebarMapBadgeFlag}`}>
								<img src={flagUrl(countryCode)} alt="" className={styles.coverImg} />
							</span>
							<span className={styles.sidebarMapBadgeLabel}>{name}</span>
						</div>
					</div>
					{/* The map card used to close with an "Explore {name} on the map" row. It was an
					    `href="#"` with preventDefault on every country AND city hub — a promise of an
					    interactive map view that does not exist yet. Removed rather than disabled: a
					    CTA that looks live and does nothing is worse than no CTA. Bring it back when
					    the map view ships. The map itself, pins and all, is unchanged. */}
				</div>

				<div className={styles.sidebarToc}>
					{/* Same key as the Hero grid's own "In this guide" — see `HeroTopics` above. */}
					<span className={styles.sidebarTocLabel}>{ui("blog.article.tocLabel", "In this guide")}</span>
					<div className={styles.sidebarTocList}>
						{items.map((t) => {
							const isActive = activeId === t.id;
							return (
								<a
									key={t.id}
									href={`#${t.id}`}
									onClick={(e) => scrollToSection(e, t.id)}
									className={styles.sidebarTocItem}
									style={{ background: isActive ? t.bg : "transparent", borderLeft: isActive ? `3px solid ${t.color}` : "3px solid transparent" }}
								>
									<span
										className={`${styles.iconCircle} ${styles.sidebarTocIconWrap}`}
										style={{
											background: isActive ? "white" : "transparent",
											color: isActive ? t.color : "#0f3e4a",
											boxShadow: isActive ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
										}}
									>
										<GuideIcon name={t.icon} fill={t.color} size={13} />
									</span>
									<span className={styles.sidebarTocText} style={{ fontWeight: isActive ? 600 : 400, color: isActive ? t.color : "#4a6e7a" }}>
										{t.label}
									</span>
								</a>
							);
						})}
					</div>
				</div>

				{partOfCountry && (
					<a
						href={partOfCountry.href}
						onClick={(e) => {
							e.preventDefault();
							partOfCountry.onClick();
						}}
						className={styles.sidebarCountryLink}
					>
						<span className={styles.sidebarCountryFlag}>
							<img src={flagUrl(partOfCountry.countryCode)} alt={`${partOfCountry.name} flag`} className={styles.coverImg} />
						</span>
						<span className={`${styles.minW0} ${styles.flex1}`}>
							<span className={styles.sidebarCountryPartOf}>{ui("guide.chrome.partOf", "Part of")}</span>
							{/* Through `ui()` like the kicker directly above it. This was raw JSX text with the
							    country name interpolated into it, so a French destination page rendered
							    "FAIT PARTIE DE / Our Mexique guide" — the label translated, the sentence
							    around it not. An English sentence built out of JSX children rather than a
							    string literal is invisible to a grep for copy, which is how it outlived two
							    i18n sweeps of this file. */}
							<span className={styles.sidebarCountryName}>{fillUi(ui("guide.chrome.ourGuide", "Our {name} guide"), { name: partOfCountry.name })}</span>
						</span>
						<svg
							width="13"
							height="13"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2.2"
							strokeLinecap="round"
							strokeLinejoin="round"
							className={styles.sidebarCountryArrow}
						>
							<path d="M7 17L17 7M17 7H9M17 7v8" />
						</svg>
					</a>
				)}

				<div className={`${styles.pad5} ${styles.sidebarCta}`}>
					<span className={styles.sidebarCtaKicker}>
						<StipplS size={13} color="currentColor" />
						<span className={styles.sidebarCtaKickerText}>{ui("guide.chrome.planItInStippl", "Plan it in Stippl")}</span>
					</span>
					<p className={styles.sidebarCtaTitle}>{fillUi(ui("blog.article.ctaTitle", "Plan {country} in Stippl"), { country: name })}</p>
					<p className={styles.sidebarCtaBody}>{ui("guide.chrome.routeDayByDayFree", "Route, day by day, stays and budget: free, even offline.")}</p>
					<button onClick={onPlan} className={styles.sidebarAiBtn}>
						<GuideIcon name="ai" fill="white" size={15} />
						{fillUi(ui("blog.section.aiCta", "Plan {name} with AI"), { name })}
					</button>
					<a href={appstoreUrl} onClick={handleTrack} target="_blank" rel="noreferrer" className={styles.sidebarDownloadBtn}>
						<span className={styles.sidebarDownloadIcon}>
							<StipplS size={13} color="white" />
						</span>
						<span className={styles.sidebarDownloadLabel}>{ui("guide.chrome.downloadApp", "Download the app")}</span>
					</a>
				</div>
			</div>
		</aside>
	);
}

// ── Closing "wave" download CTA ───────────────────────────────────────────────
// Big enough to scan. See forQrCode() for the other half of that, the payload.
const QR_SIZE = 160;

// AppsFlyer's aggregated reporting API groups by Adset but not by sub params beyond af_sub1
// (already spent on the page path) — see withAdSet's own doc comment for the full reasoning. Tags
// this end-of-page QR apart from the floating card and header modal (their own AF_ADSET
// constants), which otherwise ride the same OneLink and the same c=web_blog/web_discover campaign.
// AppStoreButtons below is left untagged — it's already identified by its own `location: "discover"`.
const WAVE_CTA_AF_ADSET = "wave_cta";

export function WaveDownloadCTA({
	photo,
	alt = "",
	title,
	body,
	country,
	kicker = "Ready when you are",
	scanLabel = "Scan me",
	scanAlt = "Scan to download the Stippl app",
}: {
	photo?: string;
	/**
	 * Caller-resolved alt for `photo` — this component only knows `country`, not the
	 * page's own display name/article title, so the fallback chain lives with each caller
	 * (country's display name for a guide, article title for a blog/itinerary page) rather
	 * than here. Defaults to `""` (the pre-123yb6937nz behaviour) for any caller that
	 * doesn't pass one.
	 */
	alt?: string;
	title: React.ReactNode;
	body: string;
	country: string;
	/** Chrome strings — the blog article pages pass localised values; guides keep the defaults. */
	kicker?: string;
	scanLabel?: string;
	scanAlt?: string;
}) {
	const qrCanvasRef = useRef<HTMLCanvasElement>(null);
	// Hydration-safe attributed URL for this placement — see usePlacementOnelinkUrl's own doc
	// comment (useAppStoreLink.ts). This component never renders the URL into SSR markup itself
	// (it only feeds the QR-drawing effect below), but that effect's own dependency array includes
	// this value, so switching from the pre-mount static placeholder to the real one still triggers
	// a redraw — same upgrade-after-mount behaviour as every other placement, for consistency.
	const trackedUrl = usePlacementOnelinkUrl(WAVE_CTA_AF_ADSET);
	// 64px put ~1.3 CSS pixels under each of this payload's 49 modules, and the code did not
	// scan. The hook already renders at devicePixelRatio, so this is the CSS box, not the bitmap.
	const qrRendered = useQrCodeCanvas(qrCanvasRef, true, forQrCode(trackedUrl), { colorDark: "#1b3d49", size: QR_SIZE });

	return (
		<section className={styles.waveSection}>
			<div className={styles.waveInner}>
				<div
					className={styles.waveMask}
					style={
						{
							aspectRatio: `${S_WAVE_ASPECT}`,
							WebkitMaskImage: S_WAVE_MASK_URL,
							maskImage: S_WAVE_MASK_URL,
							WebkitMaskRepeat: "no-repeat",
							maskRepeat: "no-repeat",
							WebkitMaskSize: "100% 100%",
							maskSize: "100% 100%",
						} as React.CSSProperties
					}
				>
					{photo ? (
						<img src={sizedPhoto(photo, 1150)} srcSet={srcSetFor(photo, 1150) || undefined} alt={alt} className={`${styles.coverImg} ${styles.waveImg}`} />
					) : (
						<PhotoFill country={country} />
					)}
				</div>
				<div className={styles.waveCard}>
					<span className={styles.waveCardKicker}>{kicker}</span>
					<h2 className={styles.waveCardTitle}>{title}</h2>
					<p className={styles.waveCardBody}>{body}</p>
					<div className={styles.waveCardCtas}>
						<AppStoreButtons location="discover" />
						<div className={styles.waveCardQr}>
							<div className={styles.waveCardQrCode}>
								<canvas ref={qrCanvasRef} width={QR_SIZE} height={QR_SIZE} style={{ display: qrRendered ? "block" : "none" }} />
								{!qrRendered && <img src={qrCode} alt={scanAlt} width={QR_SIZE} height={QR_SIZE} />}
							</div>
							<span>{scanLabel}</span>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}

// ═══════════════════════════════════════════════════════════════════════════
// Article sections (DiscoverArticle) — shared building blocks for long-form
// destination articles (e.g. "15 tips for Kyoto"). Distinct from the city/
// country guide sections above: these render plain Icons.json icons (via
// `Icon`), not the illustrated "chunky" set, since long-form articles use a
// simpler line-icon language.
// ═══════════════════════════════════════════════════════════════════════════

// ── Numbered section heading ─────────────────────────────────────────────────
export function NumberedHeading({ id, n, children }: { id?: string; n?: number; children: React.ReactNode }) {
	return (
		<h2 id={id} className={styles.numberedHeading}>
			{typeof n === "number" && <span className={styles.numberedHeadingIndex}>{String(n).padStart(2, "0")}</span>}
			<span className={styles.numberedHeadingTitle}>{children}</span>
		</h2>
	);
}

/**
 * A day heading in an itinerary article.
 *
 * Used to be "and any other `###` in article prose" too, back when `stops.length` was the only
 * signal `BlogArticleRoute` had for "this is an itinerary" — every `###` went through here
 * unconditionally, so a listicle heading wore the same green "Day N"-shaped label slot as a
 * route stop even though it never had a day to put in it. Now that articles carry their own
 * `articleType`, `DiscoverArticle`'s `renderNode` only reaches this component when
 * `article?.articleType === "itinerary"`; a listicle `###` renders through `ArticleSubHeading`
 * below instead, which has no label slot and none of this component's day styling.
 *
 * `<h3><span>Day 2</span> Khlongs, Chatuchak, rooftop bars</h3>`, with the span `display:
 * block` so it reads as a green label above a bold title. Same visual language as
 * `NumberedHeading` above, one level down.
 *
 * BOTH PARTS LIVE INSIDE THE HEADING, and that is the whole point. On a listicle `01` means
 * nothing, so it can be its own element. "Day 2" means something — "day 2 bangkok itinerary"
 * is a real query — and a heading reading only "Khlongs, Chatuchak, rooftop bars" would match
 * nothing anyone searches for. Keeping them together also gives one anchor per day rather
 * than a label floating loose from its heading.
 *
 * NOTHING IS COUNTED HERE. The label is whatever the writer wrote, ranges included ("Days 1
 * to 3"). An auto-numbered component would silently renumber every later day the moment one
 * was dropped from a route.
 */
/**
 * The "read more" signpost at the end of an itinerary stop.
 *
 * A compact line — chevron, bold "Read more:", one link — repeated identically at the end of
 * every stop so it reads as a pattern the eye can skip or follow, rather than a sentence
 * buried in a paragraph (Ilse, 2026-08-26). Its label is deliberately *not* varied per stop:
 * it is a recurring structural element, and rewording it makes three signposts read as three
 * different things.
 *
 * Internal by definition — it points at that stop's `/discover/*` hub — so no target or rel.
 */
export function ReadMore({ href, label }: { href: string; label: string }) {
	const ui = useGuideUi();
	return (
		<p className={styles.readMore}>
			<span className={styles.readMoreChevron} aria-hidden="true">
				<svg width="14" height="14" viewBox="0 0 18 18" fill="none">
					<path d="M6.5 4l5 5-5 5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
				</svg>
			</span>
			<span className={styles.readMoreLabel}>{ui("blog.section.readMore", "Read more:")}</span>{" "}
			<a href={href} className={styles.readMoreLink}>
				{label}
			</a>
		</p>
	);
}

/**
 * `explore-more` (`blockRegistry.tsx`, 123yb692y1f): the plural of `ReadMore` above — several
 * of the same signpost row under one small heading, instead of the single link a stop's own
 * `read-more` carries.
 *
 * `items` arrives pre-resolved. The directive only carries slugs (`articles="a,b,c"`), and this
 * component renders during SSR, so there is nothing here that could fetch a title — the KB
 * exporter resolves slug -> title against the published corpus at export time
 * (`resolve_block_data`'s `explore-more` branch, `tools/build_article_documents.py`) and drops
 * any slug that doesn't resolve. An empty list means none did, and this renders nothing rather
 * than a heading over no links.
 */
export function ExploreMore({ heading, items }: { heading?: string; items: { href: string; label: string }[] }) {
	if (!items.length) return null;
	return (
		<div className={styles.exploreMoreWrap}>
			{heading && <span className={styles.exploreMoreHeading}>{heading}</span>}
			{items.map((item) => (
				<ReadMore key={item.href} href={item.href} label={item.label} />
			))}
		</div>
	);
}

export function DayHeading({ id, label, children }: { id?: string; label?: string | null; children: React.ReactNode }) {
	return (
		<h3 id={id} className={styles.dayHeading}>
			{label && <span className={styles.dayHeadingLabel}>{label}</span>}
			<span className={styles.dayHeadingTitle}>{children}</span>
		</h3>
	);
}

/**
 * An `###` in a listicle article. `DayHeading`'s sibling for the article that is not a route:
 * same anchor-carrying `<h3>`, no label slot, no green "Day N" styling — that language belongs
 * to a route stop, and putting it on a listicle section is the exact bug this component exists
 * to not repeat (see `DayHeading`'s doc comment).
 *
 * Takes only `id` and `children`, not `label`: `DiscoverArticle.renderNode` recombines a stray
 * `label` (a listicle heading that happens to match the exporter's day-detection regex, e.g.
 * "Day 1 in Rome: what to see") back into `children` before it gets here, rather than dropping
 * it — this component has nowhere to put a label even if one arrived.
 */
export function ArticleSubHeading({ id, children }: { id?: string; children: React.ReactNode }) {
	return (
		<h3 id={id} className={styles.articleSubHeading}>
			{children}
		</h3>
	);
}

// ── "Stippl data" stat strip ─────────────────────────────────────────────────
export function StatsStrip({ items, label = "Stippl data" }: { items: { value: string; label: string }[]; label?: string }) {
	return (
		<div className={styles.statsStrip}>
			<div className={styles.statsStripBadge}>
				<span className={styles.statsStripMark}>
					<StipplS size={12} />
				</span>
				<span className={styles.statsStripLabel}>{label}</span>
			</div>
			<div className={styles.statsStripItems}>
				{items.map((s, i) => (
					<div key={s.label} className={styles.statsStripItem}>
						{i > 0 && <span className={styles.statsStripDivider} />}
						<div>
							<div className={styles.statsStripValue}>
								<RichText text={s.value} plain />
							</div>
							<div className={styles.statsStripItemLabel}>{s.label}</div>
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

// ── Tip callout ("Pro tip: …") ───────────────────────────────────────────────
export function TipCallout({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className={styles.tipCallout}>
			<p className={styles.tipCalloutText}>
				<span className={styles.tipCalloutLabel}>{label}: </span>
				{children}
			</p>
		</div>
	);
}

// ── Inline text link ─────────────────────────────────────────────────────────
export function InlineLink({ href = "#", children }: { href?: string; children: React.ReactNode }) {
	return (
		<a
			href={href}
			onClick={(e) => {
				if (href === "#") e.preventDefault();
			}}
			className={styles.inlineLink}
		>
			{children}
		</a>
	);
}

// ── In-article photos ────────────────────────────────────────────────────────
/**
 * A markdown table from an article body.
 *
 * `TransportTable` next to this one is a fixed three-column from/how/time shape driven by the
 * guide data; an article's tables are whatever the writer needed — a five-column transport
 * grid in one, a two-column label/value summary with no header in another. So this takes the
 * parsed table as-is.
 *
 * Until 2026-08-25 there was no table node at all and the rows collapsed into a paragraph, so
 * a route summary rendered as `| From | To | Best option |---|---|` straight onto the page.
 */
/**
 * The sign-off that tells a reader where the guide came from.
 *
 * Fixed copy, deliberately: it is a claim about how we work, and a claim that varies per
 * article is not one. The Stippl mark rather than an icon from the guide set, because this is
 * the one block that speaks as the company rather than about the destination.
 */
export function TrustFooter({ feedbackHref = "/contact" }: { feedbackHref?: string }) {
	const ui = useGuideUi();
	return (
		<div className={styles.trustFooter}>
			<span className={styles.trustFooterMark} aria-hidden="true">
				<svg viewBox={`0 0 ${S_VB.w} ${S_VB.h}`} width="52%" height="52%">
					<path d={S_PATH} fill="currentColor" />
				</svg>
			</span>
			<p className={styles.trustFooterText}>
				{ui(
					"blog.section.trustFooterText",
					"Every guide is written by the Stippl editors and checked against the trips our travellers actually plan. Spotted something out of date?"
				)}{" "}
				<a href={feedbackHref}>{ui("blog.section.trustFooterLink", "Let us know")}</a>.
			</p>
		</div>
	);
}

/**
 * A bullet run from article prose. Each item goes through `RichText`, which is the whole
 * point: these are usually hotel or restaurant picks written as `- **[name](affiliate)** (…)`,
 * and before the exporter emitted a list node they collapsed into one paragraph with the
 * dashes and the link markup showing.
 */
export function ArticleList({ items, ordered = false }: { items: string[]; ordered?: boolean }) {
	if (!items?.length) return null;
	const List = ordered ? "ol" : "ul";
	return (
		<List className={styles.articleList}>
			{items.map((item, i) => (
				<li key={i} className={styles.articleListItem}>
					<RichText text={item} />
				</li>
			))}
		</List>
	);
}

export function ArticleTable({ headers, rows }: { headers: string[]; rows: string[][] }) {
	if (!rows?.length) return null;
	// No eyebrow, deliberately. The discover blocks have one because they are sections in
	// their own right; an article table sits directly under its own H2, so a label here would
	// just repeat the heading a line above it. Markdown has no caption to take one from either,
	// and inventing an authoring convention for it is exactly the per-block decision this
	// ticket asked to avoid.
	return (
		<div className={styles.articleTableWrap}>
			<div className={styles.articleTableScroll}>
				<table className={styles.articleTable}>
					{headers.length > 0 && (
						<thead>
							<tr>
								{headers.map((h, i) => (
									<th key={i}>
										<RichText text={h} />
									</th>
								))}
							</tr>
						</thead>
					)}
					<tbody>
						{rows.map((row, r) => (
							<tr key={r}>
								{row.map((cell, c) => (
									// With no header band the first column is the label, so it carries the
									// emphasis a `<th>` would have given it.
									<td key={c} className={headers.length === 0 && c === 0 ? styles.articleTableLabel : undefined}>
										<RichText text={cell} />
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}

export function ArticlePhoto({ src, caption, alt, objectPosition = "50% 50%" }: { src: string; caption?: string; alt?: string; objectPosition?: string }) {
	// `alt` and `caption` are different jobs and used to be the same string: the alt described
	// the photo, the caption credited it, and passing one value for both printed the sentence
	// twice — once in the figcaption and again as alt text whenever the image did not load.
	return (
		<figure className={styles.articlePhoto}>
			<div className={styles.articlePhotoFrame}>
				<img
					src={sizedPhoto(src, 720)}
					srcSet={srcSetFor(src, 720) || undefined}
					alt={alt ?? caption ?? ""}
					loading="lazy"
					className={styles.coverImg}
					style={{ objectPosition }}
				/>
			</div>
			{caption && <figcaption className={styles.articlePhotoCaption}>{caption}</figcaption>}
		</figure>
	);
}

// The exporter's validator is the gate; this is the page refusing to frame an arbitrary host if a bad document ever gets through.
const EMBED_SRC_RE = /^https:\/\/embed\.stippl\.io\/\S+$|^https:\/\/www\.stippl\.io\/maps\/[a-z0-9-]+\/embed$/;

export function ArticleEmbed({ src, height, title }: { src: string; height: number | null; title: string | null }) {
	if (!EMBED_SRC_RE.test(src)) return null;
	return (
		<div className={styles.articleEmbed}>
			<iframe src={src} title={title ?? "Stippl map"} height={height ?? 500} loading="lazy" className={styles.articleEmbedFrame} />
		</div>
	);
}

export function ArticlePhotoPair({
	left,
	right,
}: {
	left: { src: string; caption?: string; objectPosition?: string };
	right: { src: string; caption?: string; objectPosition?: string };
}) {
	return (
		<div className={styles.articlePhotoPair}>
			{[left, right].map((p, i) => (
				<figure key={i}>
					<div className={styles.articlePhotoPairFrame}>
						<img
							src={sizedPhoto(p.src, 400)}
							srcSet={srcSetFor(p.src, 400) || undefined}
							alt={p.caption ?? ""}
							loading="lazy"
							className={styles.coverImg}
							style={{ objectPosition: p.objectPosition ?? "50% 50%" }}
						/>
					</div>
					{p.caption && <figcaption className={styles.articlePhotoCaption}>{p.caption}</figcaption>}
				</figure>
			))}
		</div>
	);
}

// ── Photo callout card (timing / seasonal asides) ────────────────────────────
export function PhotoCallout({
	photo,
	eyebrow,
	title,
	body,
	ctaLabel,
	ctaHref,
	objectPosition = "50% 50%",
}: {
	photo: string;
	eyebrow: string;
	title: React.ReactNode;
	body: string;
	ctaLabel: string;
	/** Omit to keep the prototype's decorative button. */
	ctaHref?: string;
	objectPosition?: string;
}) {
	return (
		<div className={styles.photoCallout}>
			<img
				src={sizedPhoto(photo, 720)}
				srcSet={srcSetFor(photo, 720) || undefined}
				alt=""
				loading="lazy"
				className={`${styles.coverImg} ${styles.photoCalloutImg}`}
				style={{ objectPosition }}
			/>
			<div className={styles.photoCalloutScrim} />
			<div className={styles.photoCalloutContent}>
				<span className={styles.photoCalloutEyebrow}>{eyebrow}</span>
				<h3 className={styles.photoCalloutTitle}>{title}</h3>
				<p className={styles.photoCalloutBody}>{body}</p>
				<a href={ctaHref ?? "#"} onClick={ctaHref ? undefined : (e) => e.preventDefault()} className={styles.photoCalloutCta}>
					{ctaLabel} →
				</a>
			</div>
		</div>
	);
}

// ── Transport table ──────────────────────────────────────────────────────────
export function TransportTable({ rows }: { rows: { from: string; how: string; time: string }[] }) {
	const ui = useGuideUi();
	return (
		<div className={styles.transportTable}>
			<div className={styles.transportTableHeaderRow}>
				{[ui("blog.section.transportFrom", "From"), ui("blog.section.transportFastest", "Fastest option"), ui("blog.section.transportTime", "Travel time")].map(
					(h) => (
						<div key={h} className={styles.transportTableHeaderCell}>
							{h}
						</div>
					)
				)}
			</div>
			{rows.map((r) => (
				<div key={r.from} className={styles.transportTableRow}>
					<div className={styles.transportTableFrom}>{r.from}</div>
					<div className={styles.transportTableHow}>{r.how}</div>
					<div className={styles.transportTableTimeCell}>
						<span className={styles.transportTableTime}>{r.time}</span>
					</div>
				</div>
			))}
		</div>
	);
}

// ── eSIM packages widget ─────────────────────────────────────────────────────
export type EsimPackage = { data: string; days: string; price: string; perGb: string; tag: string; popular?: boolean };

export function EsimPackages({
	countryCode,
	countryLabel,
	packages,
	note,
	ctaLabel,
}: {
	countryCode: string;
	countryLabel: string;
	packages: EsimPackage[];
	note: string;
	ctaLabel: string;
}) {
	const ui = useGuideUi();
	return (
		<div className={styles.esimPackages}>
			<div className={styles.esimPackagesHeader}>
				<span className={styles.esimPackagesTitle}>
					<span className={`${styles.circleBadge} ${styles.esimPackagesFlag}`}>
						<img src={flagUrl(countryCode)} alt={`${countryLabel} flag`} className={styles.coverImg} />
					</span>
					{fillUi(ui("blog.section.esimFor", "eSIM for {country}"), { country: countryLabel })}
				</span>
				<a href="#" onClick={(e) => e.preventDefault()} className={styles.esimPackagesAllLink}>
					{ui("blog.section.esimAllPackages", "All packages")}
					<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
						<path d="M5 12h14M13 6l6 6-6 6" />
					</svg>
				</a>
			</div>
			<p className={styles.esimPackagesSubtitle}>{ui("blog.section.esimSubtitle", "Data-only · ready the moment you land, no swapping SIM cards.")}</p>

			<div className={styles.esimPackagesGrid}>
				{packages.map((p) => (
					<div key={p.data} className={`${styles.esimPackage} ${p.popular ? styles.esimPackagePopular : ""}`}>
						<div className={styles.esimPackageTopRow}>
							<span className={styles.esimPackageData}>{p.data}</span>
							<span className={styles.esimPackagePrice}>{p.price}</span>
						</div>
						<div className={styles.esimPackageBottomRow}>
							<span className={styles.esimPackageDays}>{p.days}</span>
							<span className={styles.esimPackagePerGb}>{p.perGb}</span>
						</div>
						<span className={styles.esimPackageTag}>{p.tag}</span>
					</div>
				))}
			</div>

			<p className={styles.esimPackagesNote}>
				<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
					<circle cx="12" cy="12" r="10" />
					<path d="M12 16v-4M12 8h.01" />
				</svg>
				{note}
			</p>

			<button type="button" className={styles.esimPackagesCta}>
				<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
					<path d="M5 12.55a11 11 0 0 1 14.08 0M1.42 9a16 16 0 0 1 21.16 0M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01" />
				</svg>
				{ctaLabel}
			</button>
		</div>
	);
}

// ── Reading progress bar ─────────────────────────────────────────────────────
export function ReadingProgressBar() {
	const [pct, setPct] = useState(0);
	useEffect(() => {
		const onScroll = () => {
			const h = document.documentElement;
			const max = h.scrollHeight - h.clientHeight;
			setPct(max > 0 ? Math.min(100, (h.scrollTop / max) * 100) : 0);
		};
		window.addEventListener("scroll", onScroll, { passive: true });
		onScroll();
		return () => window.removeEventListener("scroll", onScroll);
	}, []);
	return (
		<div className={styles.readingProgress}>
			<div className={styles.readingProgressBar} style={{ width: `${pct}%` }} />
		</div>
	);
}

// ── Share row ─────────────────────────────────────────────────────────────────
export type ShareRowLabels = { share?: string; copyLink?: string; shareOn?: string };

export function ShareRow({ title, labels }: { title: string; labels?: ShareRowLabels }) {
	const shareLabel = labels?.share ?? "Share";
	const copyLinkLabel = labels?.copyLink ?? "Copy link";
	const shareOnLabel = labels?.shareOn ?? "Share on";
	const [copied, setCopied] = useState(false);
	const url = typeof window !== "undefined" ? window.location.href : "";
	const enc = encodeURIComponent;
	const links = [
		{
			name: "X",
			href: `https://twitter.com/intent/tweet?text=${enc(title)}&url=${enc(url)}`,
			icon: (
				<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
					<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
				</svg>
			),
		},
		{
			name: "WhatsApp",
			href: `https://wa.me/?text=${enc(title + " " + url)}`,
			icon: (
				<svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
					<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
				</svg>
			),
		},
		{
			name: "Facebook",
			href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
			icon: (
				<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
					<path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073Z" />
				</svg>
			),
		},
	];
	const copy = () => {
		if (navigator.clipboard)
			navigator.clipboard.writeText(url).then(() => {
				setCopied(true);
				setTimeout(() => setCopied(false), 1800);
			});
	};
	return (
		<div className={styles.shareRow}>
			<span className={styles.shareRowLabel}>{shareLabel}</span>
			{links.map((l) => (
				<a key={l.name} href={l.href} target="_blank" rel="noopener noreferrer" aria-label={`${shareOnLabel} ${l.name}`} className={styles.shareRowBtn}>
					{l.icon}
				</a>
			))}
			<button type="button" onClick={copy} aria-label={copyLinkLabel} className={`${styles.shareRowBtn} ${copied ? styles.shareRowBtnCopied : ""}`}>
				{copied ? (
					<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
						<path d="M20 6 9 17l-5-5" />
					</svg>
				) : (
					<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
						<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
						<path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
					</svg>
				)}
			</button>
		</div>
	);
}

// ── Article hero ─────────────────────────────────────────────────────────────
export type ArticleCrumb = { label: string; onClick?: () => void };

export function ArticleHero({
	countryCode,
	countryLabel,
	crumbs,
	title,
	description,
	author,
	meta,
	heroPhoto,
	heroPhotoAlt,
	shareTitle,
	shareLabels,
}: {
	/**
	 * Absent on a multi-country roundup (e.g. `/blog/10-best-affordable-romantic-getaways-in-europe`)
	 * — `DiscoverArticle.tsx` only resolves these off a `/discover/...` breadcrumb, which those
	 * articles don't carry. Optional so the call site can pass `undefined` honestly instead of
	 * lying about always having a code; the flag badge below is skipped entirely when absent
	 * rather than requesting `flagcdn.com/w160/.png` (a guaranteed 404) with `alt="undefined flag"`
	 * (123yb694gme).
	 */
	countryCode?: string;
	countryLabel?: string;
	crumbs: ArticleCrumb[];
	title: string;
	description: string;
	author: { name: string; role: string; avatar: string };
	meta: string;
	heroPhoto: string;
	/**
	 * `article.hero.alt`, the KB's reader-facing description of `heroPhoto` — dropped here
	 * until 123yb6937nz even though `ArticlePhoto` already renders it for every body image.
	 * Optional because `article.hero` itself is nullable; falls back to the article `title`
	 * on the one `<img>` this hero actually renders (the mobile photo below — the desktop
	 * decoration is `aria-hidden`, so it carries no alt either way).
	 */
	heroPhotoAlt?: string;
	shareTitle: string;
	/** Localised share-control strings, threaded to `ShareRow`. */
	shareLabels?: ShareRowLabels;
}) {
	return (
		<section className={styles.articleHero}>
			<div className={styles.articleHeroInner}>
				{/* Anchored inside the 1280px column, not against the full-width hero. `right`
				    resolved against `.articleHero`, which is as wide as the window, while the
				    text column stops growing at 1280 — so every pixel of window past that
				    became empty band between the intro and the swoosh, ~400px at 2000px wide.
				    `.articleHeroInner` is already `position: relative`, so the move is enough. */}
				<div className={styles.articleHeroPhotoDecoration} aria-hidden>
					<SPhotoWindow photo={heroPhoto} height={820} rotate={-28} />
				</div>

				<div className={styles.articleHeroLeft}>
					<div className={styles.articleHeroCrumbs}>
						{/* Skipped entirely, not just a blank badge, when the article has no country — see
						    the `countryCode` doc comment above (123yb694gme). */}
						{countryCode && (
							<span className={styles.articleHeroCrumbFlag}>
								<img src={flagUrl(countryCode)} alt={countryLabel ? `${countryLabel} flag` : ""} className={styles.coverImg} />
							</span>
						)}
						{crumbs.map((c, i) => {
							const isLast = i === crumbs.length - 1;
							const clickable = !!c.onClick;
							return (
								<span key={c.label} className={styles.articleHeroCrumb}>
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											c.onClick?.();
										}}
										className={`${styles.articleHeroCrumbLink} ${isLast ? styles.articleHeroCrumbLinkLast : ""} ${clickable ? styles.articleHeroCrumbLinkClickable : ""}`}
									>
										{c.label}
									</a>
									{!isLast && (
										<svg width="6" height="10" viewBox="0 0 6 10" fill="none" aria-hidden className={styles.articleHeroCrumbChevron}>
											<path d="M1 1l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
										</svg>
									)}
								</span>
							);
						})}
					</div>

					<h1 className={styles.articleHeroTitle}>{title}</h1>
					<p className={styles.articleHeroDescription}>{typeof description === "string" ? stripInlineMd(description) : description}</p>

					<div className={styles.articleHeroMetaRow}>
						<div className={styles.articleHeroAuthor}>
							<span className={styles.articleHeroAuthorAvatar}>
								<AuthorAvatar author={author} />
							</span>
							<div>
								<div className={styles.articleHeroAuthorName}>
									{author.name} · {author.role}
								</div>
								<div className={styles.articleHeroAuthorMeta}>{meta}</div>
							</div>
						</div>
						<ShareRow title={shareTitle} labels={shareLabels} />
					</div>
				</div>

				<div className={styles.articleHeroMobilePhoto}>
					<img src={sizedPhoto(heroPhoto, 900)} srcSet={srcSetFor(heroPhoto, 900) || undefined} alt={heroPhotoAlt || title} className={styles.coverImg} />
				</div>
			</div>
		</section>
	);
}

// ── Article sidebar ──────────────────────────────────────────────────────────
export type ArticleTocItem = { id: string; label: string };
export type ArticleRelatedRead = { title: string; photo?: string; objectPosition?: string; href?: string; onClick?: () => void };

// `.articleSidebarReadPhoto` is a 56x56 cover slot.
const READ_PHOTO_WIDTH = coverSlotWidth(56, 56);

export function ArticleSidebar({
	toc,
	author,
	relatedReads,
	relatedLabel = "Keep reading",
	tocLabel = "In this guide",
	authorLabel = "About the author",
	authorCtaLabel = "Open in Stippl →",
	authorCtaModalTitle = "Create a free account to open this in Stippl",
}: {
	toc: ArticleTocItem[];
	author: { name: string; role: string; avatar: string; blurb: string };
	relatedReads: ArticleRelatedRead[];
	/** Chrome strings — the blog article pages pass localised values; other callers keep the defaults. */
	relatedLabel?: string;
	tocLabel?: string;
	authorLabel?: string;
	authorCtaLabel?: string;
	authorCtaModalTitle?: string;
}) {
	const { ref, top } = useDirectionalSticky();
	const activeId = useScrollSpy(toc.map((t) => t.id));
	// The author card's CTA was `href="#"` + preventDefault — the same hardcoded-prototype
	// placeholder the related-reads links below carry a note about, except this one never got
	// a real destination. It said "Open in Stippl" and did nothing at all. It opens the signup
	// modal now, same rule as every other plan CTA (123yb691zpg).
	const authorGate = usePlanGate("blog_author_cta", "store");
	return (
		<aside className={styles.articleSidebar}>
			<div ref={ref} className={styles.articleSidebarSticky} style={{ top }}>
				<div className={styles.articleSidebarTocCard}>
					<span className={styles.articleSidebarPill}>{tocLabel}</span>
					<div className={styles.articleSidebarTocGrid}>
						{toc.map((t) => {
							const isActive = activeId === t.id;
							return (
								<a key={t.id} href={`#${t.id}`} onClick={(e) => scrollToSection(e, t.id)} className={styles.articleSidebarTocItem}>
									<span className={`${styles.articleSidebarTocDot} ${isActive ? styles.articleSidebarTocDotActive : ""}`} aria-hidden />
									<span className={`${styles.articleSidebarTocLabel} ${isActive ? styles.articleSidebarTocLabelActive : ""}`}>{t.label}</span>
								</a>
							);
						})}
					</div>
				</div>

				<div className={styles.articleSidebarAuthorCard}>
					<span className={styles.articleSidebarAuthorAvatar}>
						<AuthorAvatar author={author} />
					</span>
					<p className={styles.articleSidebarAuthorPill}>{authorLabel}</p>
					<p className={styles.articleSidebarAuthorName}>
						{author.name} · {author.role}
					</p>
					<p className={styles.articleSidebarAuthorBlurb}>
						<RichText text={author.blurb} />
					</p>
					<button type="button" onClick={authorGate.requestPlan} className={styles.articleSidebarAuthorCta}>
						{authorCtaLabel}
					</button>
					<PlanGateModal gate={authorGate} title={authorCtaModalTitle} />
				</div>

				{/* Hidden entirely when there is nothing to link. It used to render the pill and an
				    empty box, which reads as a broken component rather than an absent one. */}
				{relatedReads.length > 0 && (
					<div className={styles.articleSidebarReadsCard}>
						<span className={styles.articleSidebarPill}>{relatedLabel}</span>
						<div className={styles.articleSidebarReadsList}>
							{relatedReads.map((r) => (
								// `href="#"` with preventDefault was a placeholder from the hardcoded
								// prototype; articles carry their slug now, so these go somewhere.
								<a
									key={r.title}
									href={r.href ?? "#"}
									onClick={(e) => {
										if (!r.href) e.preventDefault();
										r.onClick?.();
									}}
									className={styles.articleSidebarReadRow}
								>
									{r.photo && (
										<span className={styles.articleSidebarReadPhoto}>
											<img
												src={sizedPhoto(r.photo, READ_PHOTO_WIDTH)}
												srcSet={srcSetFor(r.photo, READ_PHOTO_WIDTH) || undefined}
												alt=""
												className={styles.coverImg}
												style={{ objectPosition: r.objectPosition ?? "50% 50%" }}
											/>
										</span>
									)}
									<span className={styles.articleSidebarReadTitle}>{r.title}</span>
								</a>
							))}
						</div>
					</div>
				)}
			</div>
		</aside>
	);
}

// ═══════════════════════════════════════════════════════════════════════════
// Itinerary article sections (ItineraryArticle) — a route told stop by stop.
// Same reused-primitive language as the Article sections above; the hero and
// sidebar are their own components (not `ArticleHero` / `ArticleSidebar`)
// because an itinerary is built around an interactive Stippl map embed those
// two do not carry. All data comes in as props — the page owns the copy.
// ═══════════════════════════════════════════════════════════════════════════

// ── Itinerary hero (flag + breadcrumb + title + byline + tilted map + stats) ──
export type ItineraryHeroStat = { value: string; label: string; icon?: GuideIconName };

export function ItineraryHero({
	flagCode,
	crumbs,
	title,
	lead,
	author,
	authorMeta,
	stats = [],
	mapId = "itinerary-hero",
	destinations = [],
	mapBadgeLabel,
	heroPhoto,
	shareTitle,
	shareLabels,
}: {
	/** ISO alpha-2, lower-case. Omitted → the breadcrumb and map-badge flags are dropped. */
	flagCode?: string;
	crumbs: string[];
	title: string;
	lead: string;
	author: { name: string; role: string; avatar: string };
	authorMeta: string;
	/** Trip snapshot chips shown across the bottom of the hero (e.g. "14 days", "8 destinations"). */
	stats?: ItineraryHeroStat[];
	/** Unique per mounted map — react-map-gl registers by id and bare maps collide under a shared provider. */
	mapId?: string;
	/** Route stops for `TripPreviewMap`, in visiting order. Empty → the map card and mobile map are not rendered. */
	destinations?: TripPreviewMapDestination[];
	mapBadgeLabel?: string;
	heroPhoto: string;
	shareTitle: string;
	/** Localised share-control strings, threaded to `ShareRow`. */
	shareLabels?: ShareRowLabels;
}) {
	return (
		<section className={styles.itineraryHero}>
			<div className={styles.itineraryHeroDecoration} aria-hidden>
				<SPhotoWindow photo={heroPhoto} height={780} rotate={-24} fadeColor="#003f4c" />
			</div>

			<div className={styles.itineraryHeroInner}>
				<div className={styles.itineraryHeroLeft}>
					<div className={styles.itineraryHeroCrumbs}>
						{flagCode && (
							<span className={styles.itineraryHeroCrumbFlag}>
								<img src={flagUrl(flagCode)} alt={`${crumbs[crumbs.length - 1] ?? ""} flag`} className={styles.coverImg} />
							</span>
						)}
						{crumbs.map((c, i) => {
							const isLast = i === crumbs.length - 1;
							return (
								<span key={c} className={styles.itineraryHeroCrumb}>
									<span className={`${styles.itineraryHeroCrumbLabel} ${isLast ? styles.itineraryHeroCrumbLabelLast : ""}`}>{c}</span>
									{!isLast && (
										<svg width="6" height="10" viewBox="0 0 6 10" fill="none" aria-hidden className={styles.itineraryHeroCrumbChevron}>
											<path d="M1 1l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
										</svg>
									)}
								</span>
							);
						})}
					</div>

					<h1 className={styles.itineraryHeroTitle}>{title}</h1>
					<p className={styles.itineraryHeroLead}>{stripInlineMd(lead)}</p>

					<div className={styles.itineraryHeroMetaRow}>
						<div className={styles.itineraryHeroAuthor}>
							<span className={styles.itineraryHeroAuthorAvatar}>
								<AuthorAvatar author={author} />
							</span>
							<div>
								<div className={styles.itineraryHeroAuthorName}>
									{author.name} · {author.role}
								</div>
								<div className={styles.itineraryHeroAuthorMeta}>{authorMeta}</div>
							</div>
						</div>
						<ShareRow title={shareTitle} labels={shareLabels} />
					</div>
					{stats.length > 0 && (
						<div className={styles.itineraryHeroStats}>
							{stats.map((s) => (
								<div key={s.label} className={styles.itineraryHeroStat}>
									{s.icon && (
										<span className={styles.itineraryHeroStatIcon}>
											<GuideIcon name={s.icon} fill="currentColor" size={20} />
										</span>
									)}
									<div>
										<div className={styles.itineraryHeroStatValue}>{s.value}</div>
										<div className={styles.itineraryHeroStatLabel}>{s.label}</div>
									</div>
								</div>
							))}
						</div>
					)}
				</div>

				{destinations.length > 0 && (
					<div className={styles.itineraryHeroRight}>
						<div className={styles.itineraryHeroMapTilt}>
							<div className={styles.itineraryHeroMapCard}>
								<div className={styles.itineraryMapFrame}>
									<LazyTripPreviewMap id={`${mapId}-hero`} destinations={destinations} maxVisiblePins={destinations.length} showRouteLine numberedStops />
								</div>
								{mapBadgeLabel && (
									<div className={styles.itineraryHeroMapBadge}>
										{flagCode && (
											<span className={styles.itineraryHeroMapBadgeFlag}>
												<img src={flagUrl(flagCode)} alt="" className={styles.coverImg} />
											</span>
										)}
										<span className={styles.itineraryHeroMapBadgeLabel}>{mapBadgeLabel}</span>
									</div>
								)}
							</div>
						</div>
					</div>
				)}
			</div>

			{destinations.length > 0 && (
				<div className={styles.itineraryHeroMobileMap}>
					<div className={styles.itineraryMapFrame}>
						<LazyTripPreviewMap id={`${mapId}-mobile`} destinations={destinations} maxVisiblePins={destinations.length} showRouteLine numberedStops />
					</div>
				</div>
			)}
		</section>
	);
}

// ── Destination-type filter chips ─────────────────────────────────────────────
// Filters an itinerary's stops by category (cities / culture / nature / …). The
// chips read as one set: identical while inactive, and the active one fills with
// its category `color`.
export type DestinationTypeFilter = { key: string; label: string; color: string; icon?: GuideIconName; count: number };

export function DestinationTypeFilters({ filters, active, onChange }: { filters: DestinationTypeFilter[]; active: string; onChange: (key: string) => void }) {
	return (
		<div className={styles.itineraryTabs}>
			{filters.map((f) => {
				if (f.count === 0) return null;
				const on = active === f.key;
				return (
					<button
						key={f.key}
						type="button"
						id={`destination-type-${f.key}`}
						onClick={() => onChange(f.key)}
						className={`${styles.itineraryTab} ${on ? styles.itineraryTabOn : ""}`}
						style={on ? { background: f.color, borderColor: f.color } : undefined}
					>
						{f.icon && <GuideIcon name={f.icon} fill="currentColor" size={15} />}
						{f.label}
						<span className={styles.itineraryTabCount}>{f.count}</span>
					</button>
				);
			})}
		</div>
	);
}

// ── Transport connector between two itinerary stops ──────────────────────────
// A dashed rule with a centred pill ("Shinkansen to Kyoto · ~2h20"). Renders
// nothing when there is no label, so the last stop simply omits it.
export function ItineraryTransportConnector({ label }: { label?: string | null }) {
	if (!label) return null;
	return (
		<div className={styles.itineraryTransport}>
			<span className={styles.itineraryTransportRule} aria-hidden />
			<span className={styles.itineraryTransportPill}>
				<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
					<rect x="4" y="3" width="16" height="16" rx="2" />
					<path d="M4 11h16M12 3v8M8 19l-2 3M16 19l2 3" />
					<circle cx="8.5" cy="15" r="1" />
					<circle cx="15.5" cy="15" r="1" />
				</svg>
				{label}
			</span>
			<span className={styles.itineraryTransportRule} aria-hidden />
		</div>
	);
}

// ── Itinerary sidebar (map card + day-nav thumbnail strip + plan CTA) ───────
export type ItineraryNavStop = { id: string; place: string; days: string; thumb: string; accentColor: string };

export function ItinerarySidebar({
	stops,
	activeId,
	flagCode,
	mapId = "itinerary-sidebar",
	destinations = [],
	mapBadgeLabel,
	mapHref,
	cta,
	mapLinkLabel = "View route on the map",
	routeLabel = "The route",
}: {
	stops: ItineraryNavStop[];
	activeId: string | null;
	flagCode?: string;
	/** Unique per mounted map — react-map-gl registers by id and bare maps collide under a shared provider. */
	mapId?: string;
	/** Route stops for `TripPreviewMap`, in visiting order. Empty → the sidebar drops its map card. */
	destinations?: TripPreviewMapDestination[];
	mapBadgeLabel?: string;
	/** Where "View route on the map" goes. Absent → the link is not rendered (it used to be an inert `href="#"`). */
	mapHref?: string;
	/** With `href` the CTA is a real link to it; without, a button running `onClick`. */
	cta: { title: string; body: string; label: string; href?: string; onClick?: () => void };
	/** Chrome strings — the itinerary article page passes localised values. */
	mapLinkLabel?: string;
	routeLabel?: string;
}) {
	const { ref, top } = useDirectionalSticky();
	// Same face on both branches below: a link when the article names its trip, a button otherwise.
	const ctaContent = (
		<>
			{cta.label}
			<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
				<path d="M5 12h14M13 6l6 6-6 6" />
			</svg>
		</>
	);
	return (
		<aside className={styles.itinerarySidebar}>
			<div ref={ref} className={styles.itinerarySidebarSticky} style={{ top }}>
				{destinations.length > 0 && (
					<div className={styles.itinerarySidebarMapCard}>
						<div className={styles.itinerarySidebarMapFrameWrap}>
							<div className={styles.itinerarySidebarMapFrame}>
								<LazyTripPreviewMap id={`${mapId}-side`} destinations={destinations} maxVisiblePins={destinations.length} showRouteLine numberedStops />
							</div>
							{mapBadgeLabel && (
								<div className={styles.itinerarySidebarMapBadge}>
									{flagCode && (
										<span className={styles.itinerarySidebarMapBadgeFlag}>
											<img src={flagUrl(flagCode)} alt="" className={styles.coverImg} />
										</span>
									)}
									<span className={styles.itinerarySidebarMapBadgeLabel}>{mapBadgeLabel}</span>
								</div>
							)}
						</div>
						{mapHref && (
							<a href={mapHref} className={styles.itinerarySidebarMapLink}>
								{mapLinkLabel}
								<svg
									width="15"
									height="15"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2.4"
									strokeLinecap="round"
									strokeLinejoin="round"
								>
									<path d="M5 12h14M13 6l6 6-6 6" />
								</svg>
							</a>
						)}
					</div>
				)}

				<div className={styles.itinerarySidebarNav}>
					<span className={styles.itinerarySidebarNavPill}>{routeLabel}</span>
					<div className={styles.itinerarySidebarNavList}>
						{stops.map((s) => {
							const isActive = activeId === s.id;
							return (
								<a
									key={s.id}
									href={`#${s.id}`}
									onClick={(e) => scrollToSection(e, s.id)}
									className={`${styles.itinerarySidebarNavItem} ${isActive ? styles.itinerarySidebarNavItemActive : ""}`}
								>
									<span className={styles.itinerarySidebarNavThumb} style={{ outlineColor: isActive ? s.accentColor : "transparent" }}>
										<img src={sizedPhoto(s.thumb, 50)} srcSet={srcSetFor(s.thumb, 50) || undefined} alt={s.place} className={styles.coverImg} />
									</span>
									<span className={styles.itinerarySidebarNavText}>
										<span className={styles.itinerarySidebarNavDays} style={{ color: s.accentColor }}>
											{s.days}
										</span>
										<span className={styles.itinerarySidebarNavPlace}>{s.place}</span>
									</span>
									{isActive && (
										<svg
											className={styles.itinerarySidebarNavChevron}
											width="13"
											height="13"
											viewBox="0 0 24 24"
											fill="none"
											stroke={s.accentColor}
											strokeWidth="2.5"
											strokeLinecap="round"
											strokeLinejoin="round"
										>
											<path d="M9 18l6-6-6-6" />
										</svg>
									)}
								</a>
							);
						})}
					</div>
				</div>

				<div className={styles.itinerarySidebarCta}>
					<div className={styles.itinerarySidebarCtaTitle}>{cta.title}</div>
					<p className={styles.itinerarySidebarCtaBody}>{cta.body}</p>
					{cta.href ? (
						<a id="itinerary-sidebar-open" href={cta.href} className={styles.itinerarySidebarCtaBtn}>
							{ctaContent}
						</a>
					) : (
						<button type="button" id="itinerary-sidebar-open" onClick={cta.onClick} className={styles.itinerarySidebarCtaBtn}>
							{ctaContent}
						</button>
					)}
				</div>
			</div>
		</aside>
	);
}
